/**
 * Enemy templates for dungeon combat
 * Stats: Health, Attack, Defense, Speed, Range
 * Abilities: Special powers (planned for future implementation)
 * LootTable: Which loot rarity distribution to use
 * Drops: Era-appropriate items (common/uncommon/rare/epic/legendary)
 *
 * Star Essence drops by tier:
 * - Tier 1 (Common): 1-2 at 30%
 * - Tier 2 (Uncommon): 2-4 at 40%
 * - Tier 3 (Rare): 4-6 at 50%
 * - Tier 4 (Epic): 6-10 at 60%
 * - Tier 5 (Legendary): 10-15 at 70%
 * - Bosses: Guaranteed drops (15-250 based on tier)
 *
 * Gemstone drops (for Forging system):
 * - BOSSES ONLY: Gemstones drop as inventory items from bosses
 * - Tier 1 Boss: 5-10 of each type
 * - Tier 2 Boss: 8-15 of each type
 * - Tier 3 Boss: 10-20 of each type
 * - Tier 4 Boss: 15-25 of each type
 * - Tier 5 Boss: 20-30 of each type
 */

// ============================================================
// TIER 1 MOBS (Levels 5-20)
// 4 Dungeon Types, each with 4 level variants:
// - Ancient Ruins: General undead theme (original)
// - Goblin Cave: Goblin-focused with pets
// - Bandit Hideout: Human outlaws and their minions
// - Haunted Mine: Mining undead and cave creatures
// Balanced for early game - manageable HP and damage
// ============================================================

export const TIER1_MOBS = {
  // ═══════════════════════════════════════════════════════════════
  // ANCIENT RUINS MOBS (General undead theme)
  // ═══════════════════════════════════════════════════════════════

  // Forgotten Crypt (Lv 5) - Undead theme
  cryptRat: {
    id: 'cryptRat',
    name: 'Crypt Rat',
    icon: '🐀',
    baseStats: {
      health: 60,
      attack: 6,
      defense: 2,
      speed: 5,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'bone', chance: 0.50, minQty: 1, maxQty: 2 },
      { item: 'leather', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  cryptSpider: {
    id: 'cryptSpider',
    name: 'Crypt Spider',
    icon: '🕷️',
    baseStats: {
      health: 70,
      attack: 8,
      defense: 2,
      speed: 4,
      range: 1,
    },
    abilities: ['poison'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.55, minQty: 1, maxQty: 3 },
      { item: 'minorHealthPotion', chance: 0.20, minQty: 1, maxQty: 1 },
    ],
  },

  // Crumbling Tomb (Lv 10) - Skeletal theme
  tombGuardian: {
    id: 'tombGuardian',
    name: 'Tomb Guardian',
    icon: '🗿',
    baseStats: {
      health: 100,
      attack: 10,
      defense: 6,
      speed: 2,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'stone', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'rustyScrap', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  dustyMummy: {
    id: 'dustyMummy',
    name: 'Dusty Mummy',
    icon: '🧟',
    baseStats: {
      health: 90,
      attack: 12,
      defense: 4,
      speed: 2,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.60, minQty: 2, maxQty: 4 },
      { item: 'bone', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  // Sunken Vault (Lv 15) - Aquatic/flooded dungeon
  drownedZombie: {
    id: 'drownedZombie',
    name: 'Drowned Zombie',
    icon: '🧟‍♂️',
    baseStats: {
      health: 130,
      attack: 14,
      defense: 5,
      speed: 2,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'bone', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'leatherScraps', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  vaultSlime: {
    id: 'vaultSlime',
    name: 'Vault Slime',
    icon: '🟢',
    baseStats: {
      health: 150,
      attack: 10,
      defense: 8,
      speed: 1,
      range: 1,
    },
    abilities: ['acid'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'ectoplasm', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'minorHealthPotion', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  // Original Tier 1 mobs (Ancient Ruins Lv 20)
  goblin: {
    id: 'goblin',
    name: 'Goblin',
    icon: '👺',
    baseStats: {
      health: 120,
      attack: 10,
      defense: 4,
      speed: 4,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      // Star Essence (Tier 1: 1-3 at 50%)
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },

      // Consumables (common tier) - increased rates
      { item: 'bread', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'minorHealthPotion', chance: 0.25, minQty: 1, maxQty: 2 },

      // Materials (common tier - Ancient Ruins drops) - increased rates
      { item: 'bone', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'cloth', chance: 0.42, minQty: 1, maxQty: 4 },
      { item: 'leatherScraps', chance: 0.45, minQty: 1, maxQty: 4 },
      { item: 'wood', chance: 0.35, minQty: 2, maxQty: 6 },

      // Equipment (common tier only) - increased rates
      { item: 'rustyDagger', chance: 0.15, minQty: 1, maxQty: 1 },
      { item: 'woodenClub', chance: 0.12, minQty: 1, maxQty: 1 },
      { item: 'leatherVest', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'leatherCap', chance: 0.10, minQty: 1, maxQty: 1 },
    ],
  },

  skeleton: {
    id: 'skeleton',
    name: 'Skeleton',
    icon: '💀',
    baseStats: {
      health: 100,
      attack: 14,
      defense: 2,
      speed: 3,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      // Star Essence (Tier 1: 1-3 at 50%)
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },

      // Consumables (common tier) - increased rates
      { item: 'minorHealthPotion', chance: 0.20, minQty: 1, maxQty: 2 },

      // Materials (common tier - Ancient Ruins drops) - increased rates
      { item: 'bone', chance: 0.65, minQty: 1, maxQty: 4 },
      { item: 'leather', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'rustyScrap', chance: 0.40, minQty: 1, maxQty: 3 },

      // Equipment (common tier only) - increased rates
      { item: 'brokenSword', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'woodenShield', chance: 0.15, minQty: 1, maxQty: 1 },
      { item: 'ironHelmet', chance: 0.12, minQty: 1, maxQty: 1 },
    ],
  },

  wolf: {
    id: 'wolf',
    name: 'Wolf',
    icon: '🐺',
    baseStats: {
      health: 160,
      attack: 16,
      defense: 6,
      speed: 5,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      // Star Essence (Tier 1: 1-3 at 50%)
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },

      // Consumables (common tier) - increased rates
      { item: 'cookedMeat', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'minorHealthPotion', chance: 0.18, minQty: 1, maxQty: 2 },

      // Materials (common tier - Ancient Ruins drops) - increased rates
      { item: 'bone', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.55, minQty: 1, maxQty: 4 },
      { item: 'cloth', chance: 0.30, minQty: 1, maxQty: 3 },
      { item: 'wolfFang', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'fur', chance: 0.55, minQty: 1, maxQty: 4 },

      // Equipment (common tier only) - increased rates
      { item: 'leatherBoots', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'tatteredCloak', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // GOBLIN CAVE MOBS (Goblin-focused with pets)
  // ═══════════════════════════════════════════════════════════════

  // Goblin Warren (Lv 5) - Entry level goblins
  goblinWhelp: {
    id: 'goblinWhelp',
    name: 'Goblin Whelp',
    icon: '👶',
    baseStats: {
      health: 50,
      attack: 5,
      defense: 1,
      speed: 5,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'bone', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.45, minQty: 1, maxQty: 2 },
    ],
  },

  caveBat: {
    id: 'caveBat',
    name: 'Cave Bat',
    icon: '🦇',
    baseStats: {
      health: 40,
      attack: 6,
      defense: 1,
      speed: 6,
      range: 1,
    },
    abilities: ['screech'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'leather', chance: 0.50, minQty: 1, maxQty: 2 },
      { item: 'minorHealthPotion', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  // Goblin Tunnels (Lv 10) - Standard goblins
  goblinScout: {
    id: 'goblinScout',
    name: 'Goblin Scout',
    icon: '🏹',
    baseStats: {
      health: 70,
      attack: 9,
      defense: 2,
      speed: 5,
      range: 2,
    },
    abilities: ['quick_shot'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.42, minQty: 1, maxQty: 2 },
      { item: 'wood', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'rustyDagger', chance: 0.12, minQty: 1, maxQty: 1 },
    ],
  },

  goblinSapper: {
    id: 'goblinSapper',
    name: 'Goblin Sapper',
    icon: '💣',
    baseStats: {
      health: 60,
      attack: 12,
      defense: 2,
      speed: 4,
      range: 1,
    },
    abilities: ['explosive'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.42, minQty: 1, maxQty: 2 },
      { item: 'sulfur', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'coal', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Goblin Den (Lv 15) - Elite goblins
  goblinBrute: {
    id: 'goblinBrute',
    name: 'Goblin Brute',
    icon: '💪',
    baseStats: {
      health: 120,
      attack: 13,
      defense: 5,
      speed: 3,
      range: 1,
    },
    abilities: ['rage'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.48, minQty: 1, maxQty: 3 },
      { item: 'bone', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'leatherVest', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  goblinWolfRider: {
    id: 'goblinWolfRider',
    name: 'Goblin Wolf Rider',
    icon: '🐺',
    baseStats: {
      health: 100,
      attack: 14,
      defense: 4,
      speed: 6,
      range: 1,
    },
    abilities: ['charge'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.48, minQty: 1, maxQty: 3 },
      { item: 'fur', chance: 0.55, minQty: 1, maxQty: 3 },
      { item: 'wolfFang', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'leather', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  // Goblin Stronghold (Lv 20) - Boss guards
  goblinChampion: {
    id: 'goblinChampion',
    name: 'Goblin Champion',
    icon: '⚔️',
    baseStats: {
      health: 140,
      attack: 16,
      defense: 6,
      speed: 4,
      range: 1,
    },
    abilities: ['taunt', 'cleave'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.52, minQty: 1, maxQty: 3 },
      { item: 'ironOre', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'rustyScrap', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'steelSword', chance: 0.10, minQty: 1, maxQty: 1 },
    ],
  },

  goblinShaman: {
    id: 'goblinShaman',
    name: 'Goblin Shaman',
    icon: '🔮',
    baseStats: {
      health: 90,
      attack: 18,
      defense: 3,
      speed: 3,
      range: 2,
    },
    abilities: ['heal', 'curse'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.52, minQty: 1, maxQty: 3 },
      { item: 'ectoplasm', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'minorHealthPotion', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // BANDIT HIDEOUT MOBS (Human outlaws and minions)
  // ═══════════════════════════════════════════════════════════════

  // Outlaw Camp (Lv 5) - Entry level bandits
  banditThug: {
    id: 'banditThug',
    name: 'Bandit Thug',
    icon: '🥊',
    baseStats: {
      health: 55,
      attack: 6,
      defense: 2,
      speed: 4,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.38, minQty: 1, maxQty: 2 },
      { item: 'gold', chance: 0.40, minQty: 2, maxQty: 5 },
      { item: 'cloth', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'bread', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  guardDog: {
    id: 'guardDog',
    name: 'Guard Dog',
    icon: '🐕',
    baseStats: {
      health: 60,
      attack: 7,
      defense: 2,
      speed: 5,
      range: 1,
    },
    abilities: ['bite'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.38, minQty: 1, maxQty: 2 },
      { item: 'leather', chance: 0.50, minQty: 1, maxQty: 2 },
      { item: 'bone', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Bandit Outpost (Lv 10) - Standard bandits
  banditArcher: {
    id: 'banditArcher',
    name: 'Bandit Archer',
    icon: '🏹',
    baseStats: {
      health: 75,
      attack: 10,
      defense: 3,
      speed: 4,
      range: 2,
    },
    abilities: ['quick_shot'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.44, minQty: 1, maxQty: 2 },
      { item: 'wood', chance: 0.55, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'gold', chance: 0.35, minQty: 2, maxQty: 6 },
    ],
  },

  banditCutthroat: {
    id: 'banditCutthroat',
    name: 'Bandit Cutthroat',
    icon: '🗡️',
    baseStats: {
      health: 70,
      attack: 12,
      defense: 2,
      speed: 5,
      range: 1,
    },
    abilities: ['backstab'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.44, minQty: 1, maxQty: 2 },
      { item: 'gold', chance: 0.50, minQty: 3, maxQty: 8 },
      { item: 'rustyDagger', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'leatherScraps', chance: 0.40, minQty: 1, maxQty: 3 },
    ],
  },

  // Bandit Fortress (Lv 15) - Elite bandits
  banditEnforcer: {
    id: 'banditEnforcer',
    name: 'Bandit Enforcer',
    icon: '🛡️',
    baseStats: {
      health: 130,
      attack: 14,
      defense: 7,
      speed: 2,
      range: 1,
    },
    abilities: ['shield_bash'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'ironOre', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'gold', chance: 0.45, minQty: 4, maxQty: 10 },
      { item: 'woodenShield', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  banditMage: {
    id: 'banditMage',
    name: 'Hedge Mage',
    icon: '✨',
    baseStats: {
      health: 85,
      attack: 15,
      defense: 3,
      speed: 3,
      range: 2,
    },
    abilities: ['fireball'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'ectoplasm', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.55, minQty: 1, maxQty: 3 },
      { item: 'minorHealthPotion', chance: 0.28, minQty: 1, maxQty: 2 },
    ],
  },

  // Bandit Keep (Lv 20) - Boss guards
  banditVeteran: {
    id: 'banditVeteran',
    name: 'Bandit Veteran',
    icon: '⚔️',
    baseStats: {
      health: 150,
      attack: 17,
      defense: 8,
      speed: 3,
      range: 1,
    },
    abilities: ['cleave', 'parry'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.54, minQty: 1, maxQty: 3 },
      { item: 'gold', chance: 0.55, minQty: 5, maxQty: 12 },
      { item: 'ironOre', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'steelSword', chance: 0.12, minQty: 1, maxQty: 1 },
      { item: 'chainmail', chance: 0.08, minQty: 1, maxQty: 1 },
    ],
  },

  banditAssassin: {
    id: 'banditAssassin',
    name: 'Bandit Assassin',
    icon: '🥷',
    baseStats: {
      health: 100,
      attack: 20,
      defense: 4,
      speed: 6,
      range: 1,
    },
    abilities: ['stealth', 'execute'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.54, minQty: 1, maxQty: 3 },
      { item: 'gold', chance: 0.60, minQty: 6, maxQty: 15 },
      { item: 'leather', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'rustyDagger', chance: 0.20, minQty: 1, maxQty: 1 },
      { item: 'leatherBoots', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // HAUNTED MINE MOBS (Undead miners and cave creatures)
  // ═══════════════════════════════════════════════════════════════

  // Abandoned Shaft (Lv 5) - Entry level mine horrors
  mineRat: {
    id: 'mineRat',
    name: 'Mine Rat',
    icon: '🐀',
    baseStats: {
      health: 45,
      attack: 5,
      defense: 1,
      speed: 6,
      range: 1,
    },
    abilities: [],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'bone', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'coal', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  phosphorescentSlime: {
    id: 'phosphorescentSlime',
    name: 'Cave Slime',
    icon: '🟡',
    baseStats: {
      health: 65,
      attack: 5,
      defense: 4,
      speed: 1,
      range: 1,
    },
    abilities: ['acid'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'ectoplasm', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'coal', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Dark Tunnels (Lv 10) - Standard mine creatures
  undeadMiner: {
    id: 'undeadMiner',
    name: 'Undead Miner',
    icon: '⛏️',
    baseStats: {
      health: 85,
      attack: 10,
      defense: 4,
      speed: 2,
      range: 1,
    },
    abilities: ['pickaxe_swing'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.44, minQty: 1, maxQty: 2 },
      { item: 'stone', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'ironOre', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'coal', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'bone', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  mineSpider: {
    id: 'mineSpider',
    name: 'Mine Spider',
    icon: '🕷️',
    baseStats: {
      health: 70,
      attack: 11,
      defense: 2,
      speed: 4,
      range: 1,
    },
    abilities: ['poison', 'web'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.44, minQty: 1, maxQty: 2 },
      { item: 'cloth', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'minorHealthPotion', chance: 0.20, minQty: 1, maxQty: 1 },
    ],
  },

  // Collapsed Section (Lv 15) - Elite mine horrors
  phantomMiner: {
    id: 'phantomMiner',
    name: 'Phantom Miner',
    icon: '👻',
    baseStats: {
      health: 100,
      attack: 14,
      defense: 3,
      speed: 4,
      range: 1,
    },
    abilities: ['phase', 'chilling_touch'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'ectoplasm', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'soulShard', chance: 0.20, minQty: 1, maxQty: 1 },
      { item: 'ironOre', chance: 0.40, minQty: 1, maxQty: 3 },
    ],
  },

  caveHorror: {
    id: 'caveHorror',
    name: 'Cave Horror',
    icon: '🦂',
    baseStats: {
      health: 130,
      attack: 13,
      defense: 6,
      speed: 3,
      range: 1,
    },
    abilities: ['burrow', 'ambush'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'bone', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'leatherScraps', chance: 0.40, minQty: 1, maxQty: 3 },
    ],
  },

  // Cursed Depths (Lv 20) - Boss area mobs
  minerWraith: {
    id: 'minerWraith',
    name: 'Miner Wraith',
    icon: '💀',
    baseStats: {
      health: 110,
      attack: 17,
      defense: 4,
      speed: 4,
      range: 2,
    },
    abilities: ['life_drain', 'wail'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.54, minQty: 1, maxQty: 3 },
      { item: 'ectoplasm', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'soulShard', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'necroticEssence', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  rockGolem: {
    id: 'rockGolem',
    name: 'Rock Golem',
    icon: '🗿',
    baseStats: {
      health: 180,
      attack: 15,
      defense: 12,
      speed: 1,
      range: 1,
    },
    abilities: ['ground_slam', 'harden'],
    lootTable: 'common',
    drops: [
      { item: 'starEssence', chance: 0.54, minQty: 1, maxQty: 3 },
      { item: 'stone', chance: 0.65, minQty: 3, maxQty: 8 },
      { item: 'ironOre', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'rustyScrap', chance: 0.35, minQty: 1, maxQty: 3 },
    ],
  },
};

// ============================================================
// TIER 2 MOBS (Levels 25-40)
// 4 Dungeon Types:
// - Dark Catacombs: Undead/bone theme (original)
// - Frozen Crypt: Ice undead theme
// - Flooded Sewers: Sewer/plague theme
// - Orcish Stronghold: Orc warriors theme
// ============================================================

export const TIER2_MOBS = {
  // ═══════════════════════════════════════════════════════════════
  // DARK CATACOMBS MOBS (Original - Undead/bone theme)
  // ═══════════════════════════════════════════════════════════════

  // Bone Pits (Lv 25) - Bone constructs
  boneWalker: {
    id: 'boneWalker',
    name: 'Bone Walker',
    icon: '🦴',
    baseStats: {
      health: 200,
      attack: 20,
      defense: 8,
      speed: 3,
      range: 1,
    },
    abilities: [],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'bone', chance: 0.65, minQty: 2, maxQty: 5 },
      { item: 'healthPotion', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  bonePile: {
    id: 'bonePile',
    name: 'Animated Bone Pile',
    icon: '💀',
    baseStats: {
      health: 180,
      attack: 22,
      defense: 6,
      speed: 2,
      range: 1,
    },
    abilities: ['reassemble'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'bone', chance: 0.70, minQty: 3, maxQty: 6 },
      { item: 'rustyScrap', chance: 0.40, minQty: 1, maxQty: 3 },
    ],
  },

  // Cursed Ossuary (Lv 30) - Cursed undead
  cursedSkeleton: {
    id: 'cursedSkeleton',
    name: 'Cursed Skeleton',
    icon: '☠️',
    baseStats: {
      health: 220,
      attack: 26,
      defense: 10,
      speed: 3,
      range: 1,
    },
    abilities: ['curse'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'soulShard', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'healthPotion', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  ossuaryKeeper: {
    id: 'ossuaryKeeper',
    name: 'Ossuary Keeper',
    icon: '🧙',
    baseStats: {
      health: 200,
      attack: 28,
      defense: 8,
      speed: 2,
      range: 2,
    },
    abilities: ['raise_dead'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'necroticEssence', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'scrollOfFortitude', chance: 0.25, minQty: 1, maxQty: 1 },
    ],
  },

  // Necropolis (Lv 35) - Elite undead
  graveLord: {
    id: 'graveLord',
    name: 'Grave Lord',
    icon: '⚰️',
    baseStats: {
      health: 260,
      attack: 30,
      defense: 14,
      speed: 2,
      range: 1,
    },
    abilities: ['death_aura'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'darkSteel', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'chainmail', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  deathKnight: {
    id: 'deathKnight',
    name: 'Death Knight',
    icon: '🏴',
    baseStats: {
      health: 280,
      attack: 32,
      defense: 16,
      speed: 2,
      range: 1,
    },
    abilities: ['cleave'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'ironOre', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'steelHelm', chance: 0.18, minQty: 1, maxQty: 1 },
    ],
  },

  // Original Tier 2 mobs (Dark Catacombs Lv 40)
  orc: {
    id: 'orc',
    name: 'Orc Warrior',
    icon: '👹',
    baseStats: {
      health: 240,
      attack: 24,
      defense: 12,
      speed: 3,
      range: 1,
    },
    abilities: [],
    lootTable: 'uncommon',
    drops: [
      // Star Essence (Tier 2: 2-5 at 60%)
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },

      // Consumables (uncommon tier) - increased rates
      { item: 'healthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'strengthPotion', chance: 0.25, minQty: 1, maxQty: 1 },

      // Materials (uncommon tier) - increased rates
      { item: 'ironOre', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'orcTusk', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'leatherScraps', chance: 0.40, minQty: 2, maxQty: 5 },

      // Equipment (uncommon tier only) - increased rates
      { item: 'steelSword', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'warHammer', chance: 0.15, minQty: 1, maxQty: 1 },
      { item: 'chainmail', chance: 0.20, minQty: 1, maxQty: 1 },
      { item: 'steelHelm', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  wraith: {
    id: 'wraith',
    name: 'Wraith',
    icon: '👻',
    baseStats: {
      health: 180,
      attack: 30,
      defense: 4,
      speed: 4,
      range: 2,
    },
    abilities: ['phase'], // Can move through units
    lootTable: 'uncommon',
    drops: [
      // Star Essence (Tier 2: 2-5 at 60%)
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },

      // Consumables (uncommon tier) - increased rates
      { item: 'healthPotion', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'scrollOfFortitude', chance: 0.28, minQty: 1, maxQty: 1 },

      // Materials (uncommon tier) - increased rates
      { item: 'ectoplasm', chance: 0.55, minQty: 1, maxQty: 3 },
      { item: 'soulShard', chance: 0.40, minQty: 1, maxQty: 2 },

      // Equipment (uncommon tier only) - increased rates
      { item: 'arcaneHood', chance: 0.20, minQty: 1, maxQty: 1 },
      { item: 'silkCape', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'silverRing', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  darkKnight: {
    id: 'darkKnight',
    name: 'Dark Knight',
    icon: '🗡️',
    baseStats: {
      health: 280,
      attack: 28,
      defense: 16,
      speed: 2,
      range: 1,
    },
    abilities: ['shield_bash'],
    lootTable: 'uncommon',
    drops: [
      // Star Essence (Tier 2: 2-5 at 60%)
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },

      // Consumables (uncommon tier) - increased rates
      { item: 'healthPotion', chance: 0.45, minQty: 1, maxQty: 2 },
      { item: 'defensePotion', chance: 0.28, minQty: 1, maxQty: 1 },
      { item: 'scrollOfFortitude', chance: 0.25, minQty: 1, maxQty: 1 },

      // Materials (uncommon tier) - increased rates
      { item: 'darkSteel', chance: 0.42, minQty: 1, maxQty: 3 },
      { item: 'ironOre', chance: 0.50, minQty: 3, maxQty: 6 },

      // Equipment (uncommon tier only) - increased rates
      { item: 'steelShield', chance: 0.22, minQty: 1, maxQty: 1 },
      { item: 'knightHelmet', chance: 0.18, minQty: 1, maxQty: 1 },
      { item: 'scaleArmor', chance: 0.20, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // FROZEN CRYPT MOBS (Ice undead theme)
  // ═══════════════════════════════════════════════════════════════

  // Frost Cavern (Lv 25) - Entry level ice creatures
  frostZombie: {
    id: 'frostZombie',
    name: 'Frost Zombie',
    icon: '🥶',
    baseStats: {
      health: 190,
      attack: 19,
      defense: 10,
      speed: 2,
      range: 1,
    },
    abilities: ['frostbite'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'frozenBone', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'healthPotion', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  iceSpider: {
    id: 'iceSpider',
    name: 'Ice Spider',
    icon: '🕷️',
    baseStats: {
      health: 170,
      attack: 21,
      defense: 7,
      speed: 4,
      range: 1,
    },
    abilities: ['frost_web'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'frozenSilk', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'cloth', chance: 0.40, minQty: 2, maxQty: 4 },
    ],
  },

  // Glacial Tomb (Lv 30) - Mid-level ice undead
  frozenWight: {
    id: 'frozenWight',
    name: 'Frozen Wight',
    icon: '🧟',
    baseStats: {
      health: 230,
      attack: 25,
      defense: 12,
      speed: 2,
      range: 1,
    },
    abilities: ['ice_grip'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'frozenBone', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'soulShard', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  crystalElemental: {
    id: 'crystalElemental',
    name: 'Crystal Elemental',
    icon: '💎',
    baseStats: {
      health: 210,
      attack: 23,
      defense: 15,
      speed: 2,
      range: 2,
    },
    abilities: ['ice_shard'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'iceCore', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'gems', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  // Permafrost Chamber (Lv 35) - Elite ice creatures
  frostKnight: {
    id: 'frostKnight',
    name: 'Frost Knight',
    icon: '🛡️',
    baseStats: {
      health: 270,
      attack: 28,
      defense: 18,
      speed: 2,
      range: 1,
    },
    abilities: ['frozen_shield'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'darkSteel', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'chainmail', chance: 0.18, minQty: 1, maxQty: 1 },
    ],
  },

  blizzardWraith: {
    id: 'blizzardWraith',
    name: 'Blizzard Wraith',
    icon: '🌨️',
    baseStats: {
      health: 220,
      attack: 32,
      defense: 10,
      speed: 4,
      range: 2,
    },
    abilities: ['blizzard'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'ectoplasm', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'necroticEssence', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  // Frozen Crypt (Lv 40) - Boss area mobs
  iceReaper: {
    id: 'iceReaper',
    name: 'Ice Reaper',
    icon: '💀',
    baseStats: {
      health: 290,
      attack: 30,
      defense: 14,
      speed: 3,
      range: 1,
    },
    abilities: ['frozen_scythe', 'chill_aura'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.62, minQty: 2, maxQty: 5 },
      { item: 'soulShard', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'frozenBone', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'healthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  glacialGolem: {
    id: 'glacialGolem',
    name: 'Glacial Golem',
    icon: '🗿',
    baseStats: {
      health: 350,
      attack: 26,
      defense: 22,
      speed: 1,
      range: 1,
    },
    abilities: ['ice_armor', 'ground_freeze'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.62, minQty: 2, maxQty: 5 },
      { item: 'iceCore', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'stone', chance: 0.55, minQty: 3, maxQty: 6 },
      { item: 'scrollOfFortitude', chance: 0.30, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // FLOODED SEWERS MOBS (Sewer/plague theme)
  // ═══════════════════════════════════════════════════════════════

  // Sewer Entrance (Lv 25) - Entry level sewer creatures
  giantRat: {
    id: 'giantRat',
    name: 'Giant Rat',
    icon: '🐀',
    baseStats: {
      health: 180,
      attack: 20,
      defense: 6,
      speed: 5,
      range: 1,
    },
    abilities: ['disease_bite'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'leather', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'bone', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  sewerSlime: {
    id: 'sewerSlime',
    name: 'Sewer Slime',
    icon: '🟢',
    baseStats: {
      health: 220,
      attack: 16,
      defense: 12,
      speed: 1,
      range: 1,
    },
    abilities: ['toxic_splash'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'ectoplasm', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'acidGland', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Plague Tunnels (Lv 30) - Plague-infected creatures
  plagueZombie: {
    id: 'plagueZombie',
    name: 'Plague Zombie',
    icon: '🤢',
    baseStats: {
      health: 240,
      attack: 24,
      defense: 8,
      speed: 2,
      range: 1,
    },
    abilities: ['infectious_touch'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'plagueVial', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'bone', chance: 0.45, minQty: 2, maxQty: 4 },
    ],
  },

  toxicAlligator: {
    id: 'toxicAlligator',
    name: 'Toxic Alligator',
    icon: '🐊',
    baseStats: {
      health: 280,
      attack: 26,
      defense: 14,
      speed: 2,
      range: 1,
    },
    abilities: ['death_roll'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'thickHide', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'leather', chance: 0.50, minQty: 2, maxQty: 4 },
    ],
  },

  // Festering Depths (Lv 35) - Elite sewer horrors
  swampHag: {
    id: 'swampHag',
    name: 'Swamp Hag',
    icon: '🧙‍♀️',
    baseStats: {
      health: 230,
      attack: 32,
      defense: 10,
      speed: 2,
      range: 3,
    },
    abilities: ['hex', 'poison_cloud'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'witchHerb', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'healthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  sewerAbomination: {
    id: 'sewerAbomination',
    name: 'Sewer Abomination',
    icon: '👹',
    baseStats: {
      health: 320,
      attack: 28,
      defense: 14,
      speed: 2,
      range: 1,
    },
    abilities: ['putrid_slam'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'necroticEssence', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'thickHide', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  // Flooded Sewers (Lv 40) - Boss area mobs
  plagueLord: {
    id: 'plagueLord',
    name: 'Plague Lord',
    icon: '☣️',
    baseStats: {
      health: 280,
      attack: 30,
      defense: 12,
      speed: 2,
      range: 2,
    },
    abilities: ['plague_nova', 'summon_rats'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.62, minQty: 2, maxQty: 5 },
      { item: 'plagueVial', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'necroticEssence', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'scrollOfFortitude', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  ratKing: {
    id: 'ratKing',
    name: 'Rat King',
    icon: '👑',
    baseStats: {
      health: 260,
      attack: 34,
      defense: 10,
      speed: 4,
      range: 1,
    },
    abilities: ['swarm_call', 'frenzy'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.62, minQty: 2, maxQty: 5 },
      { item: 'gold', chance: 0.60, minQty: 10, maxQty: 25 },
      { item: 'leather', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'defensePotion', chance: 0.30, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // ORCISH STRONGHOLD MOBS (Orc warriors theme)
  // ═══════════════════════════════════════════════════════════════

  // Orcish Outpost (Lv 25) - Entry level orcs
  orcGrunt: {
    id: 'orcGrunt',
    name: 'Orc Grunt',
    icon: '👺',
    baseStats: {
      health: 210,
      attack: 22,
      defense: 10,
      speed: 3,
      range: 1,
    },
    abilities: [],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'ironOre', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'orcTusk', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  orcArcher: {
    id: 'orcArcher',
    name: 'Orc Archer',
    icon: '🏹',
    baseStats: {
      health: 180,
      attack: 24,
      defense: 6,
      speed: 3,
      range: 2,
    },
    abilities: ['barrage'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'wood', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'leather', chance: 0.40, minQty: 1, maxQty: 3 },
    ],
  },

  // War Camp (Lv 30) - Mid-level orcs
  orcBerserker: {
    id: 'orcBerserker',
    name: 'Orc Berserker',
    icon: '💢',
    baseStats: {
      health: 250,
      attack: 30,
      defense: 8,
      speed: 4,
      range: 1,
    },
    abilities: ['rage', 'blood_fury'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'orcTusk', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'steelSword', chance: 0.15, minQty: 1, maxQty: 1 },
    ],
  },

  warBoar: {
    id: 'warBoar',
    name: 'War Boar',
    icon: '🐗',
    baseStats: {
      health: 280,
      attack: 26,
      defense: 12,
      speed: 4,
      range: 1,
    },
    abilities: ['charge', 'gore'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.58, minQty: 2, maxQty: 5 },
      { item: 'thickHide', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'cookedMeat', chance: 0.45, minQty: 2, maxQty: 4 },
    ],
  },

  // Battle Arena (Lv 35) - Elite orc warriors
  orcWarboss: {
    id: 'orcWarboss',
    name: 'Orc Warboss',
    icon: '⚔️',
    baseStats: {
      health: 300,
      attack: 32,
      defense: 16,
      speed: 2,
      range: 1,
    },
    abilities: ['war_cry', 'cleave'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'darkSteel', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'warHammer', chance: 0.18, minQty: 1, maxQty: 1 },
    ],
  },

  orcShaman: {
    id: 'orcShaman',
    name: 'Orc Shaman',
    icon: '🔮',
    baseStats: {
      health: 220,
      attack: 30,
      defense: 10,
      speed: 2,
      range: 3,
    },
    abilities: ['lightning_bolt', 'ancestral_blessing'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'ectoplasm', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'healthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Orcish Stronghold (Lv 40) - Boss area mobs
  orcChampion: {
    id: 'orcChampion',
    name: 'Orc Champion',
    icon: '🏆',
    baseStats: {
      health: 320,
      attack: 34,
      defense: 18,
      speed: 2,
      range: 1,
    },
    abilities: ['execute', 'battle_roar'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.62, minQty: 2, maxQty: 5 },
      { item: 'orcTusk', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'chainmail', chance: 0.20, minQty: 1, maxQty: 1 },
      { item: 'strengthPotion', chance: 0.35, minQty: 1, maxQty: 1 },
    ],
  },

  orcWarlord: {
    id: 'orcWarlord',
    name: 'Orc Warlord',
    icon: '👹',
    baseStats: {
      health: 340,
      attack: 32,
      defense: 20,
      speed: 2,
      range: 1,
    },
    abilities: ['command', 'devastating_strike'],
    lootTable: 'uncommon',
    drops: [
      { item: 'starEssence', chance: 0.62, minQty: 2, maxQty: 5 },
      { item: 'darkSteel', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'ironOre', chance: 0.55, minQty: 3, maxQty: 6 },
      { item: 'steelHelm', chance: 0.20, minQty: 1, maxQty: 1 },
    ],
  },
};

// ============================================================
// TIER 3 MOBS (Levels 45-60)
// 4 Dungeon Types:
// - Corrupted Temple: Corrupted/demonic theme (original)
// - Dragon's Lair: Dragon cult and dragonkin
// - Demonic Sanctum: Pure demon theme
// - Witch's Coven: Dark magic and witchcraft
// ============================================================

export const TIER3_MOBS = {
  // ═══════════════════════════════════════════════════════════════
  // CORRUPTED TEMPLE MOBS (Original - Corrupted/demonic theme)
  // ═══════════════════════════════════════════════════════════════

  // Defiled Shrine (Lv 45) - Corrupted holy creatures
  corruptedPriest: {
    id: 'corruptedPriest',
    name: 'Corrupted Priest',
    icon: '⛪',
    baseStats: {
      health: 300,
      attack: 32,
      defense: 12,
      speed: 2,
      range: 2,
    },
    abilities: ['dark_prayer'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'soulShard', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  defiledAngel: {
    id: 'defiledAngel',
    name: 'Defiled Angel',
    icon: '👼',
    baseStats: {
      health: 320,
      attack: 36,
      defense: 10,
      speed: 4,
      range: 1,
    },
    abilities: ['fallen_grace'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'featherFragment', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'scrollOfPrecision', chance: 0.30, minQty: 1, maxQty: 1 },
    ],
  },

  // Blighted Sanctum (Lv 50) - Plague/corruption theme
  blightedMonk: {
    id: 'blightedMonk',
    name: 'Blighted Monk',
    icon: '🧘',
    baseStats: {
      health: 340,
      attack: 38,
      defense: 14,
      speed: 3,
      range: 1,
    },
    abilities: ['plague_touch'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'necroticEssence', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  plagueBearer: {
    id: 'plagueBearer',
    name: 'Plague Bearer',
    icon: '☣️',
    baseStats: {
      health: 380,
      attack: 34,
      defense: 16,
      speed: 2,
      range: 1,
    },
    abilities: ['disease_cloud'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'ectoplasm', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'swiftnessPotion', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  // Tainted Cathedral (Lv 55) - Greater demons
  demon: {
    id: 'demon',
    name: 'Lesser Demon',
    icon: '😈',
    baseStats: {
      health: 360,
      attack: 36,
      defense: 14,
      speed: 4,
      range: 1,
    },
    abilities: ['hellfire'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'demonHorn', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'sulfur', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'greaterHealthPotion', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  hellhound: {
    id: 'hellhound',
    name: 'Hellhound',
    icon: '🐕‍🦺',
    baseStats: {
      health: 320,
      attack: 42,
      defense: 10,
      speed: 6,
      range: 1,
    },
    abilities: ['flame_breath'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'sulfur', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'scrollOfPrecision', chance: 0.35, minQty: 1, maxQty: 1 },
    ],
  },

  // Original Tier 3 mobs (Corrupted Temple Lv 60)
  vampire: {
    id: 'vampire',
    name: 'Vampire Lord',
    icon: '🧛',
    baseStats: {
      health: 400,
      attack: 44,
      defense: 16,
      speed: 5,
      range: 1,
    },
    abilities: ['life_drain', 'bat_form'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'vampireFang', chance: 0.55, minQty: 1, maxQty: 2 },
      { item: 'bloodVial', chance: 0.55, minQty: 1, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'luckyCharm', chance: 0.25, minQty: 1, maxQty: 1 },
    ],
  },

  cultist: {
    id: 'cultist',
    name: 'Dark Cultist',
    icon: '🧙‍♂️',
    baseStats: {
      health: 350,
      attack: 48,
      defense: 12,
      speed: 3,
      range: 3,
    },
    abilities: ['dark_ritual', 'summon_imp'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'soulShard', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'necroticEssence', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'scrollOfFortitude', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // DRAGON'S LAIR MOBS (Dragon cult and dragonkin)
  // ═══════════════════════════════════════════════════════════════

  // Dragon Shrine (Lv 45) - Entry dragon cult
  dragonCultist: {
    id: 'dragonCultist',
    name: 'Dragon Cultist',
    icon: '🐲',
    baseStats: {
      health: 290,
      attack: 34,
      defense: 12,
      speed: 3,
      range: 2,
    },
    abilities: ['flame_bolt'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'sulfur', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'greaterHealthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  youngling: {
    id: 'youngling',
    name: 'Dragon Youngling',
    icon: '🦎',
    baseStats: {
      health: 340,
      attack: 36,
      defense: 14,
      speed: 4,
      range: 1,
    },
    abilities: ['fire_breath_small'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'dragonScale', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'sulfur', chance: 0.50, minQty: 2, maxQty: 5 },
    ],
  },

  // Ember Chambers (Lv 50) - Mid-level dragonkin
  drakeGuard: {
    id: 'drakeGuard',
    name: 'Drake Guard',
    icon: '🐉',
    baseStats: {
      health: 380,
      attack: 40,
      defense: 18,
      speed: 3,
      range: 1,
    },
    abilities: ['flame_shield'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'dragonScale', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'darkSteel', chance: 0.35, minQty: 2, maxQty: 4 },
    ],
  },

  firewyrm: {
    id: 'firewyrm',
    name: 'Firewyrm',
    icon: '🔥',
    baseStats: {
      health: 350,
      attack: 44,
      defense: 12,
      speed: 4,
      range: 2,
    },
    abilities: ['fire_stream'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'sulfur', chance: 0.60, minQty: 3, maxQty: 6 },
      { item: 'demonHorn', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  // Magma Core (Lv 55) - Elite dragonkin
  lavaDrake: {
    id: 'lavaDrake',
    name: 'Lava Drake',
    icon: '🌋',
    baseStats: {
      health: 420,
      attack: 46,
      defense: 20,
      speed: 3,
      range: 1,
    },
    abilities: ['molten_slam', 'lava_breath'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'dragonScale', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'scrollOfPrecision', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  flameKnight: {
    id: 'flameKnight',
    name: 'Flame Knight',
    icon: '⚔️',
    baseStats: {
      health: 400,
      attack: 42,
      defense: 22,
      speed: 2,
      range: 1,
    },
    abilities: ['burning_blade', 'heat_wave'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'darkSteel', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'flamingSword', chance: 0.12, minQty: 1, maxQty: 1 },
    ],
  },

  // Dragon's Lair (Lv 60) - Boss area mobs
  elderDrake: {
    id: 'elderDrake',
    name: 'Elder Drake',
    icon: '🐲',
    baseStats: {
      health: 480,
      attack: 48,
      defense: 24,
      speed: 2,
      range: 2,
    },
    abilities: ['ancient_flame', 'dragon_roar'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'dragonScale', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'dragonHeart', chance: 0.15, minQty: 1, maxQty: 1 },
      { item: 'greaterHealthPotion', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  dragonPriest: {
    id: 'dragonPriest',
    name: 'Dragon Priest',
    icon: '🧙',
    baseStats: {
      health: 380,
      attack: 52,
      defense: 16,
      speed: 2,
      range: 3,
    },
    abilities: ['dragon_blessing', 'inferno'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'soulShard', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'demonHorn', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'luckyCharm', chance: 0.25, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // DEMONIC SANCTUM MOBS (Pure demon theme)
  // ═══════════════════════════════════════════════════════════════

  // Hellgate (Lv 45) - Entry level demons
  imp: {
    id: 'imp',
    name: 'Imp',
    icon: '😈',
    baseStats: {
      health: 260,
      attack: 32,
      defense: 8,
      speed: 5,
      range: 2,
    },
    abilities: ['fire_bolt'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'sulfur', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'demonHorn', chance: 0.25, minQty: 1, maxQty: 1 },
    ],
  },

  felGuard: {
    id: 'felGuard',
    name: 'Fel Guard',
    icon: '👺',
    baseStats: {
      health: 340,
      attack: 36,
      defense: 14,
      speed: 3,
      range: 1,
    },
    abilities: ['fel_slash'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'darkSteel', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Torment Halls (Lv 50) - Mid-level demons
  succubus: {
    id: 'succubus',
    name: 'Succubus',
    icon: '💋',
    baseStats: {
      health: 320,
      attack: 42,
      defense: 10,
      speed: 4,
      range: 2,
    },
    abilities: ['charm', 'life_steal'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'soulShard', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'scrollOfPrecision', chance: 0.30, minQty: 1, maxQty: 1 },
    ],
  },

  wrathGuard: {
    id: 'wrathGuard',
    name: 'Wrath Guard',
    icon: '💢',
    baseStats: {
      health: 400,
      attack: 40,
      defense: 18,
      speed: 2,
      range: 1,
    },
    abilities: ['berserk'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'demonHorn', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'darkSteel', chance: 0.40, minQty: 2, maxQty: 4 },
    ],
  },

  // Infernal Pit (Lv 55) - Elite demons
  doomGuard: {
    id: 'doomGuard',
    name: 'Doom Guard',
    icon: '⚔️',
    baseStats: {
      health: 450,
      attack: 48,
      defense: 20,
      speed: 3,
      range: 1,
    },
    abilities: ['doom_slash', 'war_stomp'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'demonHorn', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'sulfur', chance: 0.55, minQty: 3, maxQty: 6 },
    ],
  },

  terrorFiend: {
    id: 'terrorFiend',
    name: 'Terror Fiend',
    icon: '👁️',
    baseStats: {
      health: 380,
      attack: 52,
      defense: 14,
      speed: 4,
      range: 2,
    },
    abilities: ['fear_aura', 'shadow_bolt'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'soulShard', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'necroticEssence', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  // Demonic Sanctum (Lv 60) - Boss area mobs
  demonLord: {
    id: 'demonLord',
    name: 'Demon Lord',
    icon: '👿',
    baseStats: {
      health: 500,
      attack: 50,
      defense: 22,
      speed: 3,
      range: 1,
    },
    abilities: ['hellfire', 'summon_demons'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'demonHorn', chance: 0.60, minQty: 2, maxQty: 4 },
      { item: 'bloodVial', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  felCaster: {
    id: 'felCaster',
    name: 'Fel Caster',
    icon: '🔥',
    baseStats: {
      health: 360,
      attack: 56,
      defense: 12,
      speed: 2,
      range: 3,
    },
    abilities: ['fel_fire', 'rain_of_fire'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'sulfur', chance: 0.60, minQty: 3, maxQty: 6 },
      { item: 'soulShard', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'scrollOfFortitude', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // WITCH'S COVEN MOBS (Dark magic and witchcraft)
  // ═══════════════════════════════════════════════════════════════

  // Cursed Grove (Lv 45) - Entry level witch cult
  hexedServant: {
    id: 'hexedServant',
    name: 'Hexed Servant',
    icon: '🧟',
    baseStats: {
      health: 280,
      attack: 30,
      defense: 10,
      speed: 3,
      range: 1,
    },
    abilities: ['curse'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'witchHerb', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'bone', chance: 0.40, minQty: 2, maxQty: 4 },
    ],
  },

  cursedhound: {
    id: 'cursedhound',
    name: 'Cursed Hound',
    icon: '🐕',
    baseStats: {
      health: 310,
      attack: 34,
      defense: 12,
      speed: 5,
      range: 1,
    },
    abilities: ['curse_bite'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.65, minQty: 4, maxQty: 7 },
      { item: 'leather', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'bone', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  // Ritual Chamber (Lv 50) - Mid-level witches
  covenAcolyte: {
    id: 'covenAcolyte',
    name: 'Coven Acolyte',
    icon: '🧙‍♀️',
    baseStats: {
      health: 300,
      attack: 40,
      defense: 10,
      speed: 3,
      range: 3,
    },
    abilities: ['hex', 'dark_bolt'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'witchHerb', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'greaterHealthPotion', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  scarecrow: {
    id: 'scarecrow',
    name: 'Animate Scarecrow',
    icon: '🎃',
    baseStats: {
      health: 340,
      attack: 36,
      defense: 14,
      speed: 2,
      range: 1,
    },
    abilities: ['fear_touch'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.68, minQty: 4, maxQty: 8 },
      { item: 'cloth', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'wood', chance: 0.40, minQty: 2, maxQty: 4 },
    ],
  },

  // Dark Sanctum (Lv 55) - Elite witch cult
  blackWitch: {
    id: 'blackWitch',
    name: 'Black Witch',
    icon: '🧹',
    baseStats: {
      health: 350,
      attack: 50,
      defense: 12,
      speed: 3,
      range: 3,
    },
    abilities: ['mass_hex', 'shadow_bolt'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'soulShard', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'witchHerb', chance: 0.55, minQty: 2, maxQty: 5 },
    ],
  },

  nightHag: {
    id: 'nightHag',
    name: 'Night Hag',
    icon: '👵',
    baseStats: {
      health: 380,
      attack: 46,
      defense: 14,
      speed: 2,
      range: 2,
    },
    abilities: ['nightmare', 'life_drain'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.70, minQty: 4, maxQty: 8 },
      { item: 'necroticEssence', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'bloodVial', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Witch's Coven (Lv 60) - Boss area mobs
  covenMatriarch: {
    id: 'covenMatriarch',
    name: 'Coven Matriarch',
    icon: '👑',
    baseStats: {
      health: 420,
      attack: 52,
      defense: 16,
      speed: 2,
      range: 3,
    },
    abilities: ['dark_ritual', 'curse_storm'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'soulShard', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'witchHerb', chance: 0.60, minQty: 3, maxQty: 6 },
      { item: 'luckyCharm', chance: 0.25, minQty: 1, maxQty: 1 },
    ],
  },

  hauntedArmor: {
    id: 'hauntedArmor',
    name: 'Haunted Armor',
    icon: '🛡️',
    baseStats: {
      health: 480,
      attack: 44,
      defense: 26,
      speed: 2,
      range: 1,
    },
    abilities: ['phantom_strike', 'soul_bind'],
    lootTable: 'rare',
    drops: [
      { item: 'starEssence', chance: 0.72, minQty: 5, maxQty: 9 },
      { item: 'darkSteel', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'ectoplasm', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'plateArmor', chance: 0.12, minQty: 1, maxQty: 1 },
    ],
  },
};

// ============================================================
// BOSS MOBS (End of dungeon)
// Bosses drop better loot from their tier + chance at next tier
// Each tier has multiple boss options for variety
// ============================================================

export const BOSS_MOBS = {
  // ═══════════════════════════════════════════════════════════════
  // TIER 1 BOSSES (Levels 5-20)
  // ═══════════════════════════════════════════════════════════════

  // Ancient Ruins Boss (Original)
  boss_ogre: {
    id: 'boss_ogre',
    name: 'Ogre Chieftain',
    icon: '👿',
    baseStats: {
      health: 200,
      attack: 20,
      defense: 10,
      speed: 2,
      range: 1,
    },
    abilities: ['smash'], // AOE attack
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 1 Boss: 15-25 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 15, maxQty: 25 },

      // Gemstones (Tier 1 Boss: 100% guaranteed, 5-10 qty each)
      { item: 'ruby', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'sapphire', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'emerald', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'topaz', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'amethyst', chance: 1.0, minQty: 5, maxQty: 10 },

      // Cubes (Tier 1 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 2, maxQty: 4 },
      { item: 'masterCube', chance: 0.25, minQty: 1, maxQty: 2 },

      // Consumables (common tier + uncommon chance)
      { item: 'minorHealthPotion', chance: 0.80, minQty: 3, maxQty: 5 },
      { item: 'healthPotion', chance: 0.30, minQty: 1, maxQty: 2 }, // Uncommon tier
      { item: 'strengthPotion', chance: 0.20, minQty: 1, maxQty: 1 }, // Uncommon tier

      // Materials (common tier + boss materials - Ancient Ruins drops)
      { item: 'bone', chance: 0.75, minQty: 3, maxQty: 6 },
      { item: 'leather', chance: 0.70, minQty: 3, maxQty: 5 },
      { item: 'cloth', chance: 0.65, minQty: 2, maxQty: 4 },
      { item: 'ogreHeart', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'thickHide', chance: 0.70, minQty: 2, maxQty: 4 },

      // Equipment (common tier + small uncommon chance)
      { item: 'woodenClub', chance: 0.60, minQty: 1, maxQty: 1 },
      { item: 'leatherVest', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'steelSword', chance: 0.15, minQty: 1, maxQty: 1 }, // Uncommon tier (rare drop)
    ],
  },

  // Goblin Cave Boss
  boss_goblinKing: {
    id: 'boss_goblinKing',
    name: 'Goblin King',
    icon: '👑',
    baseStats: {
      health: 180,
      attack: 18,
      defense: 8,
      speed: 4,
      range: 1,
    },
    abilities: ['rally', 'summon_goblins', 'dirty_trick'], // Summons minions, buffs allies
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 1 Boss: 15-25 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 15, maxQty: 25 },

      // Gemstones (Tier 1 Boss: 100% guaranteed, 5-10 qty each)
      { item: 'ruby', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'sapphire', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'emerald', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'topaz', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'amethyst', chance: 1.0, minQty: 5, maxQty: 10 },

      // Cubes (Tier 1 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 2, maxQty: 4 },
      { item: 'masterCube', chance: 0.25, minQty: 1, maxQty: 2 },

      // Consumables
      { item: 'minorHealthPotion', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'healthPotion', chance: 0.25, minQty: 1, maxQty: 2 },

      // Materials - Goblin themed
      { item: 'gold', chance: 0.80, minQty: 15, maxQty: 30 },
      { item: 'leather', chance: 0.70, minQty: 2, maxQty: 5 },
      { item: 'wolfFang', chance: 0.60, minQty: 2, maxQty: 4 },
      { item: 'fur', chance: 0.65, minQty: 2, maxQty: 5 },
      { item: 'goblinCrown', chance: 0.40, minQty: 1, maxQty: 1 },

      // Equipment
      { item: 'rustyDagger', chance: 0.65, minQty: 1, maxQty: 1 },
      { item: 'leatherVest', chance: 0.55, minQty: 1, maxQty: 1 },
      { item: 'leatherBoots', chance: 0.45, minQty: 1, maxQty: 1 },
    ],
  },

  // Bandit Hideout Boss
  boss_banditLord: {
    id: 'boss_banditLord',
    name: 'Bandit Lord',
    icon: '🏴‍☠️',
    baseStats: {
      health: 220,
      attack: 22,
      defense: 12,
      speed: 3,
      range: 1,
    },
    abilities: ['command', 'parry', 'execute'], // Combat veteran abilities
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 1 Boss: 15-25 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 15, maxQty: 25 },

      // Gemstones (Tier 1 Boss: 100% guaranteed, 5-10 qty each)
      { item: 'ruby', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'sapphire', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'emerald', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'topaz', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'amethyst', chance: 1.0, minQty: 5, maxQty: 10 },

      // Cubes (Tier 1 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 2, maxQty: 4 },
      { item: 'masterCube', chance: 0.28, minQty: 1, maxQty: 2 },

      // Consumables
      { item: 'minorHealthPotion', chance: 0.80, minQty: 3, maxQty: 5 },
      { item: 'healthPotion', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'strengthPotion', chance: 0.25, minQty: 1, maxQty: 1 },

      // Materials - Bandit themed (lots of gold!)
      { item: 'gold', chance: 1.0, minQty: 25, maxQty: 50 },
      { item: 'ironOre', chance: 0.65, minQty: 2, maxQty: 5 },
      { item: 'leather', chance: 0.70, minQty: 3, maxQty: 6 },
      { item: 'banditMedallion', chance: 0.45, minQty: 1, maxQty: 1 },

      // Equipment - Better quality (bandits have stolen goods)
      { item: 'steelSword', chance: 0.55, minQty: 1, maxQty: 1 },
      { item: 'woodenShield', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'chainmail', chance: 0.20, minQty: 1, maxQty: 1 },
      { item: 'leatherBoots', chance: 0.45, minQty: 1, maxQty: 1 },
    ],
  },

  // Haunted Mine Boss
  boss_mineOverseer: {
    id: 'boss_mineOverseer',
    name: 'Spectral Overseer',
    icon: '👻',
    baseStats: {
      health: 160,
      attack: 24,
      defense: 6,
      speed: 4,
      range: 2,
    },
    abilities: ['soul_drain', 'summon_undead', 'terrifying_wail'], // Ghost abilities
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 1 Boss: 15-25 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 15, maxQty: 25 },

      // Gemstones (Tier 1 Boss: 100% guaranteed, 5-10 qty each)
      { item: 'ruby', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'sapphire', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'emerald', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'topaz', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'amethyst', chance: 1.0, minQty: 5, maxQty: 10 },

      // Cubes (Tier 1 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 2, maxQty: 4 },
      { item: 'masterCube', chance: 0.30, minQty: 1, maxQty: 2 },

      // Consumables
      { item: 'minorHealthPotion', chance: 0.75, minQty: 3, maxQty: 5 },
      { item: 'healthPotion', chance: 0.30, minQty: 1, maxQty: 2 },

      // Materials - Mining themed (lots of ore!)
      { item: 'stone', chance: 0.85, minQty: 5, maxQty: 12 },
      { item: 'ironOre', chance: 0.80, minQty: 4, maxQty: 8 },
      { item: 'coal', chance: 0.75, minQty: 3, maxQty: 7 },
      { item: 'ectoplasm', chance: 0.65, minQty: 2, maxQty: 4 },
      { item: 'soulShard', chance: 0.50, minQty: 1, maxQty: 2 },
      { item: 'cursedPickaxe', chance: 0.35, minQty: 1, maxQty: 1 },

      // Equipment
      { item: 'ironHelmet', chance: 0.55, minQty: 1, maxQty: 1 },
      { item: 'leatherVest', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'arcaneHood', chance: 0.18, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // TIER 2 BOSSES (Levels 25-40)
  // ═══════════════════════════════════════════════════════════════

  // Tier 2 Boss (Uncommon dungeon)
  boss_lich: {
    id: 'boss_lich',
    name: 'Ancient Lich',
    icon: '🧙‍♂️',
    baseStats: {
      health: 150,
      attack: 25,
      defense: 5,
      speed: 3,
      range: 3,
    },
    abilities: ['summon', 'drain'], // Summons minions, life drain
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 2 Boss: 30-50 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 30, maxQty: 50 },

      // Gemstones (Tier 2 Boss: 100% guaranteed, 8-15 qty each)
      { item: 'ruby', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'sapphire', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'emerald', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'topaz', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'amethyst', chance: 1.0, minQty: 8, maxQty: 15 },

      // Cubes (Tier 2 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 3, maxQty: 5 },
      { item: 'masterCube', chance: 0.60, minQty: 2, maxQty: 3 },
      { item: 'meisterCube', chance: 0.15, minQty: 1, maxQty: 2 },

      // Consumables (uncommon tier + rare chance)
      { item: 'healthPotion', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'greaterHealthPotion', chance: 0.35, minQty: 1, maxQty: 2 }, // Rare tier
      { item: 'scrollOfFortitude', chance: 0.60, minQty: 1, maxQty: 3 },
      { item: 'scrollOfPrecision', chance: 0.25, minQty: 1, maxQty: 1 }, // Rare tier
      { item: 'cleanSlateScroll', chance: 0.15, minQty: 1, maxQty: 1 }, // Epic tier (rare drop)

      // Materials (uncommon tier + boss materials)
      { item: 'lichPhylactery', chance: 0.40, minQty: 1, maxQty: 1 },
      { item: 'necroticEssence', chance: 0.75, minQty: 2, maxQty: 4 },
      { item: 'ectoplasm', chance: 0.60, minQty: 3, maxQty: 6 },

      // Equipment (uncommon tier + small rare chance)
      { item: 'chainmail', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'steelShield', chance: 0.45, minQty: 1, maxQty: 1 },
      { item: 'plateArmor', chance: 0.15, minQty: 1, maxQty: 1 }, // Rare tier (rare drop)
    ],
  },

  // Tier 3 Boss (Rare dungeon)
  boss_dragon: {
    id: 'boss_dragon',
    name: 'Ancient Dragon',
    icon: '🐉',
    baseStats: {
      health: 300,
      attack: 30,
      defense: 15,
      speed: 2,
      range: 2,
    },
    abilities: ['fire_breath', 'wing_buffet', 'tail_sweep'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 3 Boss: 50-80 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 50, maxQty: 80 },

      // Gemstones (Tier 3 Boss: 100% guaranteed, 10-20 qty each)
      { item: 'ruby', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'sapphire', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'emerald', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'topaz', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'amethyst', chance: 1.0, minQty: 10, maxQty: 20 },

      // Cubes (Tier 3 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'masterCube', chance: 0.80, minQty: 3, maxQty: 4 },
      { item: 'meisterCube', chance: 0.40, minQty: 2, maxQty: 3 },
      { item: 'redCube', chance: 0.10, minQty: 1, maxQty: 2 },

      // Consumables (rare tier + epic chance)
      { item: 'greaterHealthPotion', chance: 0.90, minQty: 4, maxQty: 8 },
      { item: 'superiorHealthPotion', chance: 0.30, minQty: 1, maxQty: 2 }, // Epic tier
      { item: 'elixirOfPower', chance: 0.25, minQty: 1, maxQty: 1 }, // Epic tier
      { item: 'scrollOfPrecision', chance: 0.70, minQty: 2, maxQty: 4 },
      { item: 'luckyCharm', chance: 0.60, minQty: 2, maxQty: 3 },
      { item: 'cleanSlateScroll', chance: 0.25, minQty: 1, maxQty: 1 }, // Epic tier

      // Materials (rare tier + boss materials)
      { item: 'dragonScale', chance: 0.80, minQty: 3, maxQty: 6 },
      { item: 'dragonHeart', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'ancientBone', chance: 0.70, minQty: 2, maxQty: 5 },

      // Equipment (rare tier + small epic chance)
      { item: 'flamingSword', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'dragonscale', chance: 0.45, minQty: 1, maxQty: 1 },
      { item: 'dragonHelm', chance: 0.40, minQty: 1, maxQty: 1 },
      { item: 'dragonslayerBlade', chance: 0.12, minQty: 1, maxQty: 1 }, // Epic tier (rare drop)
    ],
  },

  // Tier 4 Boss (Epic dungeon - Abyssal Depths)
  boss_abyssalLord: {
    id: 'boss_abyssalLord',
    name: 'Abyssal Lord',
    icon: '🦑',
    baseStats: {
      health: 500,
      attack: 40,
      defense: 25,
      speed: 2,
      range: 2,
    },
    abilities: ['tentacle_slam', 'ink_cloud', 'deep_call'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 4 Boss: 80-120 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 80, maxQty: 120 },

      // Gemstones (Tier 4 Boss: 100% guaranteed, 15-25 qty each)
      { item: 'ruby', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'sapphire', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'emerald', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'topaz', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'amethyst', chance: 1.0, minQty: 15, maxQty: 25 },

      // Cubes (Tier 4 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 5, maxQty: 8 },
      { item: 'masterCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'meisterCube', chance: 0.70, minQty: 3, maxQty: 5 },
      { item: 'redCube', chance: 0.35, minQty: 2, maxQty: 3 },
      { item: 'blackCube', chance: 0.05, minQty: 1, maxQty: 1 },

      // Consumables (epic tier + legendary chance)
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'elixirOfPower', chance: 0.85, minQty: 3, maxQty: 5 },
      { item: 'blessingToken', chance: 0.70, minQty: 3, maxQty: 5 },
      { item: 'luckyCharm', chance: 0.75, minQty: 3, maxQty: 4 },
      { item: 'cleanSlateScroll', chance: 0.40, minQty: 1, maxQty: 2 }, // Epic tier

      // Materials (epic tier - guaranteed divineEssence + legendary chance)
      { item: 'divineEssence', chance: 1.0, minQty: 3, maxQty: 5 },
      { item: 'eldritchHeart', chance: 0.50, minQty: 1, maxQty: 2 },
      { item: 'voidEssence', chance: 0.80, minQty: 2, maxQty: 4 },
      { item: 'cosmicOre', chance: 0.20, minQty: 1, maxQty: 1 }, // Rare legendary drop

      // Equipment (epic tier + legendary chance)
      { item: 'dragonslayerBlade', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'titaniumPlate', chance: 0.45, minQty: 1, maxQty: 1 },
      { item: 'titanHelm', chance: 0.40, minQty: 1, maxQty: 1 },
      { item: 'soulreaver', chance: 0.12, minQty: 1, maxQty: 1 }, // Legendary tier
      { item: 'eternalGuardian', chance: 0.10, minQty: 1, maxQty: 1 }, // Legendary tier
    ],
  },

  // Tier 5 Boss (Legendary dungeon - Void Citadel)
  boss_voidGod: {
    id: 'boss_voidGod',
    name: 'The Void God',
    icon: '🕳️',
    baseStats: {
      health: 800,
      attack: 50,
      defense: 30,
      speed: 1,
      range: 3,
    },
    abilities: ['annihilation', 'void_nova', 'reality_collapse', 'eternal_darkness'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      // Star Essence (Tier 5 Boss: 150-250 guaranteed)
      { item: 'starEssence', chance: 1.0, minQty: 150, maxQty: 250 },

      // Gemstones (Tier 5 Boss: 100% guaranteed, 20-30 qty each)
      { item: 'ruby', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'sapphire', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'emerald', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'topaz', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'amethyst', chance: 1.0, minQty: 20, maxQty: 30 },

      // Cubes (Tier 5 Boss)
      { item: 'occultCube', chance: 1.0, minQty: 6, maxQty: 10 },
      { item: 'masterCube', chance: 1.0, minQty: 6, maxQty: 8 },
      { item: 'meisterCube', chance: 1.0, minQty: 5, maxQty: 7 },
      { item: 'redCube', chance: 0.60, minQty: 3, maxQty: 5 },
      { item: 'blackCube', chance: 0.20, minQty: 1, maxQty: 2 },

      // Consumables (legendary tier)
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'elixirOfPower', chance: 1.0, minQty: 5, maxQty: 8 },
      { item: 'blessingToken', chance: 0.90, minQty: 5, maxQty: 8 },
      { item: 'luckyCharm', chance: 0.85, minQty: 4, maxQty: 6 },
      { item: 'cleanSlateScroll', chance: 0.60, minQty: 1, maxQty: 3 }, // Epic tier (common at this level)

      // Materials (legendary tier - guaranteed cosmicOre + eterniumIngot)
      { item: 'cosmicOre', chance: 1.0, minQty: 2, maxQty: 4 },
      { item: 'eterniumIngot', chance: 1.0, minQty: 1, maxQty: 3 },
      { item: 'divineEssence', chance: 0.80, minQty: 4, maxQty: 6 },
      { item: 'eldritchHeart', chance: 0.70, minQty: 2, maxQty: 3 },

      // Equipment (legendary tier + mythic chance)
      { item: 'soulreaver', chance: 0.50, minQty: 1, maxQty: 1 },
      { item: 'cosmicScythe', chance: 0.45, minQty: 1, maxQty: 1 },
      { item: 'eternalGuardian', chance: 0.40, minQty: 1, maxQty: 1 },
      { item: 'firebrand', chance: 0.15, minQty: 1, maxQty: 1 }, // Mythic tier
      { item: 'frostblade', chance: 0.12, minQty: 1, maxQty: 1 }, // Mythic tier
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // TIER 2 NEW BOSSES (Frozen Crypt, Flooded Sewers, Orcish Stronghold)
  // ═══════════════════════════════════════════════════════════════

  // Frozen Crypt Boss
  boss_frostLich: {
    id: 'boss_frostLich',
    name: 'Frost Lich',
    icon: '❄️',
    baseStats: {
      health: 180,
      attack: 28,
      defense: 8,
      speed: 2,
      range: 3,
    },
    abilities: ['blizzard', 'ice_tomb', 'frost_nova'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 30, maxQty: 50 },
      { item: 'ruby', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'sapphire', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'emerald', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'topaz', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'amethyst', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'occultCube', chance: 1.0, minQty: 3, maxQty: 5 },
      { item: 'masterCube', chance: 0.55, minQty: 2, maxQty: 3 },
      { item: 'healthPotion', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'frozenBone', chance: 0.80, minQty: 3, maxQty: 6 },
      { item: 'iceCore', chance: 0.60, minQty: 2, maxQty: 4 },
      { item: 'necroticEssence', chance: 0.65, minQty: 2, maxQty: 4 },
    ],
  },

  // Flooded Sewers Boss
  boss_plagueRat: {
    id: 'boss_plagueRat',
    name: 'The Plague Rat',
    icon: '🐀',
    baseStats: {
      health: 200,
      attack: 26,
      defense: 10,
      speed: 4,
      range: 1,
    },
    abilities: ['plague_bite', 'summon_swarm', 'disease_aura'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 30, maxQty: 50 },
      { item: 'ruby', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'sapphire', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'emerald', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'topaz', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'amethyst', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'occultCube', chance: 1.0, minQty: 3, maxQty: 5 },
      { item: 'masterCube', chance: 0.58, minQty: 2, maxQty: 3 },
      { item: 'healthPotion', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'plagueVial', chance: 0.75, minQty: 2, maxQty: 4 },
      { item: 'acidGland', chance: 0.60, minQty: 2, maxQty: 4 },
      { item: 'gold', chance: 0.90, minQty: 15, maxQty: 30 },
    ],
  },

  // Orcish Stronghold Boss
  boss_orcWarchief: {
    id: 'boss_orcWarchief',
    name: 'Orc Warchief',
    icon: '👹',
    baseStats: {
      health: 220,
      attack: 30,
      defense: 14,
      speed: 2,
      range: 1,
    },
    abilities: ['battle_cry', 'execute', 'rally_troops'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 30, maxQty: 50 },
      { item: 'ruby', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'sapphire', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'emerald', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'topaz', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'amethyst', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'occultCube', chance: 1.0, minQty: 3, maxQty: 5 },
      { item: 'masterCube', chance: 0.62, minQty: 2, maxQty: 3 },
      { item: 'healthPotion', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'orcTusk', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'darkSteel', chance: 0.70, minQty: 2, maxQty: 4 },
      { item: 'warHammer', chance: 0.35, minQty: 1, maxQty: 1 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // TIER 3 NEW BOSSES (Dragon's Lair, Demonic Sanctum, Witch's Coven)
  // ═══════════════════════════════════════════════════════════════

  // Dragon's Lair Boss
  boss_elderWyrm: {
    id: 'boss_elderWyrm',
    name: 'Elder Wyrm',
    icon: '🐉',
    baseStats: {
      health: 350,
      attack: 35,
      defense: 18,
      speed: 2,
      range: 2,
    },
    abilities: ['fire_breath', 'tail_sweep', 'molten_rage'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 50, maxQty: 80 },
      { item: 'ruby', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'sapphire', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'emerald', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'topaz', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'amethyst', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'occultCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'masterCube', chance: 0.80, minQty: 3, maxQty: 4 },
      { item: 'meisterCube', chance: 0.35, minQty: 2, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.90, minQty: 4, maxQty: 8 },
      { item: 'dragonScale', chance: 0.85, minQty: 4, maxQty: 7 },
      { item: 'dragonHeart', chance: 0.45, minQty: 1, maxQty: 1 },
      { item: 'sulfur', chance: 0.75, minQty: 4, maxQty: 8 },
    ],
  },

  // Demonic Sanctum Boss
  boss_demonPrince: {
    id: 'boss_demonPrince',
    name: 'Demon Prince',
    icon: '👿',
    baseStats: {
      health: 320,
      attack: 38,
      defense: 14,
      speed: 3,
      range: 2,
    },
    abilities: ['hellfire', 'summon_demons', 'fel_corruption'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 50, maxQty: 80 },
      { item: 'ruby', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'sapphire', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'emerald', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'topaz', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'amethyst', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'occultCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'masterCube', chance: 0.78, minQty: 3, maxQty: 4 },
      { item: 'meisterCube', chance: 0.38, minQty: 2, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.90, minQty: 4, maxQty: 8 },
      { item: 'demonHorn', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'sulfur', chance: 0.80, minQty: 4, maxQty: 8 },
      { item: 'bloodVial', chance: 0.60, minQty: 2, maxQty: 4 },
    ],
  },

  // Witch's Coven Boss
  boss_grandWitch: {
    id: 'boss_grandWitch',
    name: 'Grand Witch',
    icon: '🧙‍♀️',
    baseStats: {
      health: 280,
      attack: 40,
      defense: 12,
      speed: 2,
      range: 3,
    },
    abilities: ['mass_hex', 'dark_ritual', 'summon_familiar'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 50, maxQty: 80 },
      { item: 'ruby', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'sapphire', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'emerald', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'topaz', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'amethyst', chance: 1.0, minQty: 10, maxQty: 20 },
      { item: 'occultCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'masterCube', chance: 0.82, minQty: 3, maxQty: 4 },
      { item: 'meisterCube', chance: 0.40, minQty: 2, maxQty: 3 },
      { item: 'greaterHealthPotion', chance: 0.90, minQty: 4, maxQty: 8 },
      { item: 'witchHerb', chance: 0.85, minQty: 4, maxQty: 8 },
      { item: 'soulShard', chance: 0.70, minQty: 2, maxQty: 4 },
      { item: 'necroticEssence', chance: 0.60, minQty: 2, maxQty: 4 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // TIER 4 NEW BOSSES (Infernal Fortress, Shadow Realm, Eldritch Library)
  // ═══════════════════════════════════════════════════════════════

  // Infernal Fortress Boss
  boss_infernoLord: {
    id: 'boss_infernoLord',
    name: 'Inferno Lord',
    icon: '🔥',
    baseStats: {
      health: 550,
      attack: 45,
      defense: 28,
      speed: 2,
      range: 2,
    },
    abilities: ['inferno_storm', 'flame_prison', 'molten_eruption'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 80, maxQty: 120 },
      { item: 'ruby', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'sapphire', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'emerald', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'topaz', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'amethyst', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'occultCube', chance: 1.0, minQty: 5, maxQty: 8 },
      { item: 'masterCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'meisterCube', chance: 0.65, minQty: 3, maxQty: 5 },
      { item: 'redCube', chance: 0.30, minQty: 2, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'demonHorn', chance: 0.90, minQty: 4, maxQty: 7 },
      { item: 'divineEssence', chance: 0.75, minQty: 2, maxQty: 4 },
      { item: 'starMetal', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Shadow Realm Boss
  boss_nightmareKing: {
    id: 'boss_nightmareKing',
    name: 'Nightmare King',
    icon: '👑',
    baseStats: {
      health: 520,
      attack: 48,
      defense: 24,
      speed: 3,
      range: 2,
    },
    abilities: ['realm_of_nightmares', 'shadow_clones', 'terror_incarnate'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 80, maxQty: 120 },
      { item: 'ruby', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'sapphire', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'emerald', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'topaz', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'amethyst', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'occultCube', chance: 1.0, minQty: 5, maxQty: 8 },
      { item: 'masterCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'meisterCube', chance: 0.68, minQty: 3, maxQty: 5 },
      { item: 'redCube', chance: 0.32, minQty: 2, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'soulShard', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'eldritchHeart', chance: 0.55, minQty: 1, maxQty: 2 },
      { item: 'voidEssence', chance: 0.70, minQty: 2, maxQty: 4 },
    ],
  },

  // Eldritch Library Boss
  boss_madArchivist: {
    id: 'boss_madArchivist',
    name: 'The Mad Archivist',
    icon: '📚',
    baseStats: {
      health: 480,
      attack: 52,
      defense: 22,
      speed: 2,
      range: 3,
    },
    abilities: ['forbidden_knowledge', 'mind_shatter', 'summon_tomes'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 80, maxQty: 120 },
      { item: 'ruby', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'sapphire', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'emerald', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'topaz', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'amethyst', chance: 1.0, minQty: 15, maxQty: 25 },
      { item: 'occultCube', chance: 1.0, minQty: 5, maxQty: 8 },
      { item: 'masterCube', chance: 1.0, minQty: 4, maxQty: 6 },
      { item: 'meisterCube', chance: 0.72, minQty: 3, maxQty: 5 },
      { item: 'redCube', chance: 0.35, minQty: 2, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 5, maxQty: 10 },
      { item: 'ancientPage', chance: 0.90, minQty: 4, maxQty: 8 },
      { item: 'eldritchHeart', chance: 0.60, minQty: 1, maxQty: 2 },
      { item: 'cosmicFragment', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // TIER 5 NEW BOSSES (Celestial Ruins, Dimensional Rift, Chaos Nexus)
  // ═══════════════════════════════════════════════════════════════

  // Celestial Ruins Boss
  boss_fallenSeraph: {
    id: 'boss_fallenSeraph',
    name: 'Fallen Seraph',
    icon: '👼',
    baseStats: {
      health: 850,
      attack: 55,
      defense: 32,
      speed: 2,
      range: 2,
    },
    abilities: ['divine_judgement', 'fallen_grace', 'holy_corruption'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 150, maxQty: 250 },
      { item: 'ruby', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'sapphire', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'emerald', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'topaz', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'amethyst', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'occultCube', chance: 1.0, minQty: 6, maxQty: 10 },
      { item: 'masterCube', chance: 1.0, minQty: 6, maxQty: 8 },
      { item: 'meisterCube', chance: 1.0, minQty: 5, maxQty: 7 },
      { item: 'redCube', chance: 0.55, minQty: 3, maxQty: 5 },
      { item: 'blackCube', chance: 0.18, minQty: 1, maxQty: 2 },
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'divineEssence', chance: 1.0, minQty: 4, maxQty: 7 },
      { item: 'featherFragment', chance: 0.85, minQty: 3, maxQty: 6 },
      { item: 'cosmicOre', chance: 0.75, minQty: 2, maxQty: 3 },
    ],
  },

  // Dimensional Rift Boss
  boss_realityBreaker: {
    id: 'boss_realityBreaker',
    name: 'Reality Breaker',
    icon: '💥',
    baseStats: {
      health: 820,
      attack: 58,
      defense: 28,
      speed: 3,
      range: 3,
    },
    abilities: ['dimension_shatter', 'reality_collapse', 'infinite_recursion'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 150, maxQty: 250 },
      { item: 'ruby', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'sapphire', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'emerald', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'topaz', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'amethyst', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'occultCube', chance: 1.0, minQty: 6, maxQty: 10 },
      { item: 'masterCube', chance: 1.0, minQty: 6, maxQty: 8 },
      { item: 'meisterCube', chance: 1.0, minQty: 5, maxQty: 7 },
      { item: 'redCube', chance: 0.58, minQty: 3, maxQty: 5 },
      { item: 'blackCube', chance: 0.20, minQty: 1, maxQty: 2 },
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'cosmicFragment', chance: 1.0, minQty: 4, maxQty: 7 },
      { item: 'cosmicOre', chance: 0.80, minQty: 2, maxQty: 4 },
      { item: 'eterniumIngot', chance: 0.60, minQty: 1, maxQty: 2 },
    ],
  },

  // Chaos Nexus Boss
  boss_entropySovereign: {
    id: 'boss_entropySovereign',
    name: 'Entropy Sovereign',
    icon: '🌀',
    baseStats: {
      health: 880,
      attack: 54,
      defense: 30,
      speed: 2,
      range: 3,
    },
    abilities: ['chaos_unbound', 'entropy_wave', 'creation_destruction'],
    lootTable: 'boss',
    isBoss: true,
    drops: [
      { item: 'starEssence', chance: 1.0, minQty: 150, maxQty: 250 },
      { item: 'ruby', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'sapphire', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'emerald', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'topaz', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'amethyst', chance: 1.0, minQty: 20, maxQty: 30 },
      { item: 'occultCube', chance: 1.0, minQty: 6, maxQty: 10 },
      { item: 'masterCube', chance: 1.0, minQty: 6, maxQty: 8 },
      { item: 'meisterCube', chance: 1.0, minQty: 5, maxQty: 7 },
      { item: 'redCube', chance: 0.62, minQty: 3, maxQty: 5 },
      { item: 'blackCube', chance: 0.22, minQty: 1, maxQty: 2 },
      { item: 'superiorHealthPotion', chance: 1.0, minQty: 8, maxQty: 15 },
      { item: 'eldritchHeart', chance: 0.85, minQty: 2, maxQty: 4 },
      { item: 'voidEssence', chance: 0.90, minQty: 3, maxQty: 6 },
      { item: 'eterniumIngot', chance: 0.65, minQty: 1, maxQty: 3 },
    ],
  },
};

// ============================================================
// TIER 4 MOBS (Levels 65-80)
// 4 Dungeon Types:
// - Abyssal Depths: Abyssal/shadow theme (original)
// - Infernal Fortress: Burning legion theme
// - Shadow Realm: Pure shadow/nightmare theme
// - Eldritch Library: Forbidden knowledge theme
// ============================================================

export const TIER4_MOBS = {
  // ═══════════════════════════════════════════════════════════════
  // ABYSSAL DEPTHS MOBS (Original - Abyssal/shadow theme)
  // ═══════════════════════════════════════════════════════════════

  // Shadow Chasm (Lv 65) - Shadow creatures
  shadowFiend: {
    id: 'shadowFiend',
    name: 'Shadow Fiend',
    icon: '👤',
    baseStats: {
      health: 420,
      attack: 46,
      defense: 18,
      speed: 5,
      range: 1,
    },
    abilities: ['shadow_step'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'ectoplasm', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  darkWraith: {
    id: 'darkWraith',
    name: 'Dark Wraith',
    icon: '🌫️',
    baseStats: {
      health: 380,
      attack: 52,
      defense: 14,
      speed: 4,
      range: 2,
    },
    abilities: ['life_siphon'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'necroticEssence', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'scrollOfPrecision', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Nightmare Grotto (Lv 70) - Dream/horror creatures
  nightmareBeast: {
    id: 'nightmareBeast',
    name: 'Nightmare Beast',
    icon: '🐲',
    baseStats: {
      health: 500,
      attack: 54,
      defense: 22,
      speed: 3,
      range: 1,
    },
    abilities: ['terror_aura'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'voidEssence', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 0.55, minQty: 1, maxQty: 3 },
    ],
  },

  fearWeaver: {
    id: 'fearWeaver',
    name: 'Fear Weaver',
    icon: '🕸️',
    baseStats: {
      health: 440,
      attack: 58,
      defense: 16,
      speed: 3,
      range: 3,
    },
    abilities: ['mind_shatter'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'soulShard', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'elixirOfPower', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  // Demon Pit (Lv 75) - Greater demons and abyssal horrors
  pitLord: {
    id: 'pitLord',
    name: 'Pit Lord',
    icon: '👺',
    baseStats: {
      health: 580,
      attack: 60,
      defense: 26,
      speed: 2,
      range: 1,
    },
    abilities: ['infernal_rage', 'ground_slam'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'demonHorn', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'divineEssence', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'superiorHealthPotion', chance: 0.60, minQty: 1, maxQty: 3 },
    ],
  },

  voidStalker: {
    id: 'voidStalker',
    name: 'Void Stalker',
    icon: '👁️',
    baseStats: {
      health: 480,
      attack: 56,
      defense: 20,
      speed: 5,
      range: 2,
    },
    abilities: ['blink', 'void_strike'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'voidEssence', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'cosmicFragment', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'scrollOfPrecision', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Original Tier 4 mobs (Abyssal Depths Lv 80)
  abyssalKnight: {
    id: 'abyssalKnight',
    name: 'Abyssal Knight',
    icon: '⚔️',
    baseStats: {
      health: 600,
      attack: 62,
      defense: 30,
      speed: 2,
      range: 1,
    },
    abilities: ['shield_wall', 'crushing_blow'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'divineEssence', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'starMetal', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'darkSteel', chance: 0.40, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.60, minQty: 1, maxQty: 3 },
      { item: 'scrollOfFortitude', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  shadowLord: {
    id: 'shadowLord',
    name: 'Shadow Lord',
    icon: '🌑',
    baseStats: {
      health: 520,
      attack: 68,
      defense: 24,
      speed: 4,
      range: 2,
    },
    abilities: ['shadow_cloak', 'dark_bolt'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'eldritchHeart', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'voidEssence', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'necroticEssence', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'luckyCharm', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'blessingToken', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // INFERNAL FORTRESS MOBS (Burning legion theme)
  // ═══════════════════════════════════════════════════════════════

  // Burning Gate (Lv 65) - Entry infernal forces
  flameSoldier: {
    id: 'flameSoldier',
    name: 'Flame Soldier',
    icon: '🔥',
    baseStats: {
      health: 440,
      attack: 48,
      defense: 20,
      speed: 3,
      range: 1,
    },
    abilities: ['burning_strike'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'sulfur', chance: 0.50, minQty: 3, maxQty: 6 },
      { item: 'superiorHealthPotion', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  infernalHound: {
    id: 'infernalHound',
    name: 'Infernal Hound',
    icon: '🐕‍🦺',
    baseStats: {
      health: 400,
      attack: 52,
      defense: 16,
      speed: 5,
      range: 1,
    },
    abilities: ['flame_pounce'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'demonHorn', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'thickHide', chance: 0.40, minQty: 2, maxQty: 4 },
    ],
  },

  // Magma Halls (Lv 70) - Mid-level infernal
  lavaDemon: {
    id: 'lavaDemon',
    name: 'Lava Demon',
    icon: '👹',
    baseStats: {
      health: 520,
      attack: 56,
      defense: 22,
      speed: 3,
      range: 1,
    },
    abilities: ['molten_touch', 'eruption'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'demonHorn', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'sulfur', chance: 0.55, minQty: 3, maxQty: 7 },
    ],
  },

  fireElemental: {
    id: 'fireElemental',
    name: 'Fire Elemental',
    icon: '🌟',
    baseStats: {
      health: 460,
      attack: 60,
      defense: 18,
      speed: 4,
      range: 2,
    },
    abilities: ['fire_nova'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'iceCore', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'elixirOfPower', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  // Infernal Core (Lv 75) - Elite infernal
  flameLord: {
    id: 'flameLord',
    name: 'Flame Lord',
    icon: '🔱',
    baseStats: {
      health: 600,
      attack: 62,
      defense: 26,
      speed: 2,
      range: 2,
    },
    abilities: ['inferno_storm', 'flame_shield'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'demonHorn', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'divineEssence', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  moltenGolem: {
    id: 'moltenGolem',
    name: 'Molten Golem',
    icon: '🗿',
    baseStats: {
      health: 700,
      attack: 58,
      defense: 32,
      speed: 1,
      range: 1,
    },
    abilities: ['lava_slam', 'heat_aura'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'starMetal', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'darkSteel', chance: 0.45, minQty: 2, maxQty: 5 },
    ],
  },

  // Infernal Fortress (Lv 80) - Boss area mobs
  archDemon: {
    id: 'archDemon',
    name: 'Arch Demon',
    icon: '😈',
    baseStats: {
      health: 640,
      attack: 66,
      defense: 28,
      speed: 3,
      range: 1,
    },
    abilities: ['doom_fire', 'demon_rage'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'demonHorn', chance: 0.60, minQty: 3, maxQty: 5 },
      { item: 'bloodVial', chance: 0.40, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.60, minQty: 1, maxQty: 3 },
    ],
  },

  infernoKnight: {
    id: 'infernoKnight',
    name: 'Inferno Knight',
    icon: '⚔️',
    baseStats: {
      health: 680,
      attack: 64,
      defense: 32,
      speed: 2,
      range: 1,
    },
    abilities: ['burning_blade', 'immolation'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'darkSteel', chance: 0.50, minQty: 3, maxQty: 6 },
      { item: 'starMetal', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'scrollOfFortitude', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // SHADOW REALM MOBS (Pure shadow/nightmare theme)
  // ═══════════════════════════════════════════════════════════════

  // Twilight Gate (Lv 65) - Entry shadow creatures
  shadowStalker: {
    id: 'shadowStalker',
    name: 'Shadow Stalker',
    icon: '👤',
    baseStats: {
      health: 410,
      attack: 50,
      defense: 16,
      speed: 5,
      range: 1,
    },
    abilities: ['shadow_strike', 'fade'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'ectoplasm', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  nightmareSpawn: {
    id: 'nightmareSpawn',
    name: 'Nightmare Spawn',
    icon: '😱',
    baseStats: {
      health: 380,
      attack: 54,
      defense: 14,
      speed: 4,
      range: 2,
    },
    abilities: ['fear_bolt'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'soulShard', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'necroticEssence', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  // Dream Abyss (Lv 70) - Mid-level nightmare
  sleepParalyzer: {
    id: 'sleepParalyzer',
    name: 'Sleep Paralyzer',
    icon: '💤',
    baseStats: {
      health: 450,
      attack: 58,
      defense: 18,
      speed: 3,
      range: 3,
    },
    abilities: ['sleep', 'nightmare_vision'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'soulShard', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'elixirOfPower', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  phantasm: {
    id: 'phantasm',
    name: 'Phantasm',
    icon: '👻',
    baseStats: {
      health: 420,
      attack: 56,
      defense: 12,
      speed: 5,
      range: 2,
    },
    abilities: ['phase', 'ghostly_wail'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'ectoplasm', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'voidEssence', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Void Threshold (Lv 75) - Elite shadow
  umbralReaper: {
    id: 'umbralReaper',
    name: 'Umbral Reaper',
    icon: '💀',
    baseStats: {
      health: 540,
      attack: 64,
      defense: 20,
      speed: 4,
      range: 1,
    },
    abilities: ['reap', 'shadow_scythe'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'soulShard', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'divineEssence', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  dreameater: {
    id: 'dreameater',
    name: 'Dream Eater',
    icon: '🌙',
    baseStats: {
      health: 480,
      attack: 68,
      defense: 18,
      speed: 3,
      range: 2,
    },
    abilities: ['consume_dreams', 'terror_aura'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'eldritchHeart', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'cosmicFragment', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  // Shadow Realm (Lv 80) - Boss area mobs
  nightmarePrince: {
    id: 'nightmarePrince',
    name: 'Nightmare Prince',
    icon: '👑',
    baseStats: {
      health: 580,
      attack: 70,
      defense: 24,
      speed: 3,
      range: 2,
    },
    abilities: ['realm_of_fear', 'shadow_domain'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'soulShard', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'eldritchHeart', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'luckyCharm', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  voidbornHorror: {
    id: 'voidbornHorror',
    name: 'Voidborn Horror',
    icon: '🕳️',
    baseStats: {
      health: 620,
      attack: 66,
      defense: 26,
      speed: 2,
      range: 2,
    },
    abilities: ['void_touch', 'reality_tear'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'voidEssence', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'cosmicFragment', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'blessingToken', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // ELDRITCH LIBRARY MOBS (Forbidden knowledge theme)
  // ═══════════════════════════════════════════════════════════════

  // Forbidden Archives (Lv 65) - Entry knowledge horrors
  animatedTome: {
    id: 'animatedTome',
    name: 'Animated Tome',
    icon: '📕',
    baseStats: {
      health: 380,
      attack: 52,
      defense: 14,
      speed: 3,
      range: 3,
    },
    abilities: ['spell_cast'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'ancientPage', chance: 0.50, minQty: 1, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  inkHorror: {
    id: 'inkHorror',
    name: 'Ink Horror',
    icon: '🖤',
    baseStats: {
      health: 420,
      attack: 48,
      defense: 18,
      speed: 3,
      range: 1,
    },
    abilities: ['ink_splash', 'blind'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.75, minQty: 6, maxQty: 10 },
      { item: 'ectoplasm', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'cloth', chance: 0.40, minQty: 2, maxQty: 5 },
    ],
  },

  // Maddening Stacks (Lv 70) - Mid-level knowledge
  knowledgeSeeker: {
    id: 'knowledgeSeeker',
    name: 'Knowledge Seeker',
    icon: '🔍',
    baseStats: {
      health: 440,
      attack: 56,
      defense: 16,
      speed: 4,
      range: 2,
    },
    abilities: ['mind_probe', 'knowledge_drain'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'soulShard', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'ancientPage', chance: 0.45, minQty: 1, maxQty: 3 },
    ],
  },

  madScholar: {
    id: 'madScholar',
    name: 'Mad Scholar',
    icon: '🧙‍♂️',
    baseStats: {
      health: 400,
      attack: 62,
      defense: 14,
      speed: 3,
      range: 3,
    },
    abilities: ['arcane_blast', 'insanity'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.78, minQty: 6, maxQty: 11 },
      { item: 'necroticEssence', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'scrollOfPrecision', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  // Sanctum of Madness (Lv 75) - Elite knowledge
  mindFlayer: {
    id: 'mindFlayer',
    name: 'Mind Flayer',
    icon: '🐙',
    baseStats: {
      health: 500,
      attack: 66,
      defense: 18,
      speed: 3,
      range: 2,
    },
    abilities: ['mind_blast', 'psychic_drain'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'eldritchHeart', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'soulShard', chance: 0.55, minQty: 2, maxQty: 4 },
    ],
  },

  forbiddenOne: {
    id: 'forbiddenOne',
    name: 'The Forbidden One',
    icon: '👁️',
    baseStats: {
      health: 540,
      attack: 64,
      defense: 22,
      speed: 2,
      range: 3,
    },
    abilities: ['forbidden_knowledge', 'reality_warp'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.80, minQty: 7, maxQty: 12 },
      { item: 'cosmicFragment', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'divineEssence', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Eldritch Library (Lv 80) - Boss area mobs
  archScholar: {
    id: 'archScholar',
    name: 'Arch Scholar',
    icon: '📚',
    baseStats: {
      health: 560,
      attack: 68,
      defense: 20,
      speed: 2,
      range: 3,
    },
    abilities: ['grand_arcana', 'tome_strike'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'ancientPage', chance: 0.60, minQty: 2, maxQty: 5 },
      { item: 'necroticEssence', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'elixirOfPower', chance: 0.40, minQty: 1, maxQty: 2 },
    ],
  },

  boundEntity: {
    id: 'boundEntity',
    name: 'Bound Entity',
    icon: '🔗',
    baseStats: {
      health: 640,
      attack: 64,
      defense: 26,
      speed: 2,
      range: 2,
    },
    abilities: ['chains_of_binding', 'eldritch_scream'],
    lootTable: 'epic',
    drops: [
      { item: 'starEssence', chance: 0.82, minQty: 7, maxQty: 13 },
      { item: 'eldritchHeart', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'voidEssence', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'scrollOfFortitude', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },
};

// ============================================================
// TIER 5 MOBS (Levels 85-100)
// 4 Dungeon Types:
// - Void Citadel: Void/cosmic theme (original)
// - Celestial Ruins: Divine/angelic corruption theme
// - Dimensional Rift: Reality-bending theme
// - Chaos Nexus: Pure entropy/chaos theme
// ============================================================

export const TIER5_MOBS = {
  // ═══════════════════════════════════════════════════════════════
  // VOID CITADEL MOBS (Original - Void/cosmic theme)
  // ═══════════════════════════════════════════════════════════════

  // Rift Nexus (Lv 85) - Dimensional rifters
  riftWalker: {
    id: 'riftWalker',
    name: 'Rift Walker',
    icon: '🌀',
    baseStats: {
      health: 600,
      attack: 64,
      defense: 22,
      speed: 5,
      range: 2,
    },
    abilities: ['dimension_hop', 'rift_slash'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'voidEssence', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.65, minQty: 2, maxQty: 4 },
    ],
  },

  dimensionalTear: {
    id: 'dimensionalTear',
    name: 'Dimensional Tear',
    icon: '💫',
    baseStats: {
      health: 550,
      attack: 70,
      defense: 18,
      speed: 4,
      range: 3,
    },
    abilities: ['spatial_rend'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'cosmicFragment', chance: 0.40, minQty: 2, maxQty: 4 },
      { item: 'elixirOfPower', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  // Astral Prison (Lv 90) - Imprisoned cosmic entities
  astralWarden: {
    id: 'astralWarden',
    name: 'Astral Warden',
    icon: '⚖️',
    baseStats: {
      health: 700,
      attack: 68,
      defense: 28,
      speed: 3,
      range: 1,
    },
    abilities: ['chains_of_binding', 'astral_barrier'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'divineEssence', chance: 0.40, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.70, minQty: 2, maxQty: 5 },
      { item: 'blessingToken', chance: 0.45, minQty: 1, maxQty: 2 },
    ],
  },

  prisonedOne: {
    id: 'prisonedOne',
    name: 'The Prisoned One',
    icon: '🔗',
    baseStats: {
      health: 650,
      attack: 74,
      defense: 24,
      speed: 3,
      range: 2,
    },
    abilities: ['broken_shackles', 'rage_unleashed'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'eldritchHeart', chance: 0.35, minQty: 1, maxQty: 3 },
      { item: 'scrollOfFortitude', chance: 0.55, minQty: 2, maxQty: 4 },
    ],
  },

  // Cosmic Throne (Lv 95) - Star-born rulers
  cosmicHorror: {
    id: 'cosmicHorror',
    name: 'Cosmic Horror',
    icon: '🌌',
    baseStats: {
      health: 750,
      attack: 76,
      defense: 26,
      speed: 3,
      range: 3,
    },
    abilities: ['reality_warp', 'mind_shatter', 'cosmic_ray'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'cosmicOre', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'cosmicFragment', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.75, minQty: 2, maxQty: 5 },
      { item: 'elixirOfPower', chance: 0.55, minQty: 1, maxQty: 3 },
    ],
  },

  starforgedTitan: {
    id: 'starforgedTitan',
    name: 'Starforged Titan',
    icon: '🗿',
    baseStats: {
      health: 900,
      attack: 72,
      defense: 36,
      speed: 2,
      range: 1,
    },
    abilities: ['stellar_crush', 'nova_shield'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'starMetal', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'eterniumIngot', chance: 0.20, minQty: 1, maxQty: 2 },
      { item: 'blessingToken', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  // Original Tier 5 mobs (Void Citadel Lv 100)
  eternityWarden: {
    id: 'eternityWarden',
    name: 'Eternity Warden',
    icon: '⏳',
    baseStats: {
      health: 950,
      attack: 78,
      defense: 40,
      speed: 2,
      range: 1,
    },
    abilities: ['time_stop', 'immortal_stance'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'eterniumIngot', chance: 0.28, minQty: 1, maxQty: 2 },
      { item: 'divineEssence', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'starMetal', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 0.80, minQty: 2, maxQty: 5 },
      { item: 'scrollOfFortitude', chance: 0.65, minQty: 2, maxQty: 4 },
    ],
  },

  voidEmperor: {
    id: 'voidEmperor',
    name: 'Void Emperor',
    icon: '👑',
    baseStats: {
      health: 880,
      attack: 84,
      defense: 34,
      speed: 3,
      range: 2,
    },
    abilities: ['void_command', 'emperor_decree', 'dimensional_rift'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'cosmicOre', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'eterniumIngot', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'eldritchHeart', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'voidEssence', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'elixirOfPower', chance: 0.65, minQty: 1, maxQty: 3 },
      { item: 'luckyCharm', chance: 0.60, minQty: 1, maxQty: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // CELESTIAL RUINS MOBS (Divine/angelic corruption theme)
  // ═══════════════════════════════════════════════════════════════

  // Fallen Sanctum (Lv 85) - Entry corrupted divine
  fallenAngel: {
    id: 'fallenAngel',
    name: 'Fallen Angel',
    icon: '👼',
    baseStats: {
      health: 620,
      attack: 66,
      defense: 24,
      speed: 4,
      range: 2,
    },
    abilities: ['divine_fall'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'featherFragment', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.65, minQty: 2, maxQty: 4 },
    ],
  },

  corruptedSeraph: {
    id: 'corruptedSeraph',
    name: 'Corrupted Seraph',
    icon: '✨',
    baseStats: {
      health: 580,
      attack: 70,
      defense: 20,
      speed: 4,
      range: 3,
    },
    abilities: ['holy_corruption'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'divineEssence', chance: 0.40, minQty: 2, maxQty: 4 },
      { item: 'elixirOfPower', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  // Broken Paradise (Lv 90) - Mid-level divine
  wrathAngel: {
    id: 'wrathAngel',
    name: 'Wrath Angel',
    icon: '⚔️',
    baseStats: {
      health: 700,
      attack: 72,
      defense: 26,
      speed: 3,
      range: 1,
    },
    abilities: ['righteous_fury', 'divine_smite'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'featherFragment', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'starMetal', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  hollowGuardian: {
    id: 'hollowGuardian',
    name: 'Hollow Guardian',
    icon: '🛡️',
    baseStats: {
      health: 800,
      attack: 66,
      defense: 34,
      speed: 2,
      range: 1,
    },
    abilities: ['divine_shield', 'judgement'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'divineEssence', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'blessingToken', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  // Shattered Throne (Lv 95) - Elite divine
  archSeraph: {
    id: 'archSeraph',
    name: 'Arch Seraph',
    icon: '👑',
    baseStats: {
      health: 780,
      attack: 78,
      defense: 28,
      speed: 3,
      range: 2,
    },
    abilities: ['divine_storm', 'celestial_wrath'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'divineEssence', chance: 0.55, minQty: 3, maxQty: 5 },
      { item: 'cosmicOre', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  voidTouchedAngel: {
    id: 'voidTouchedAngel',
    name: 'Void-Touched Angel',
    icon: '🌌',
    baseStats: {
      health: 720,
      attack: 82,
      defense: 24,
      speed: 4,
      range: 2,
    },
    abilities: ['void_light', 'corruption_spread'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'voidEssence', chance: 0.50, minQty: 2, maxQty: 4 },
      { item: 'eldritchHeart', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  // Celestial Ruins (Lv 100) - Boss area mobs
  fallenDivinity: {
    id: 'fallenDivinity',
    name: 'Fallen Divinity',
    icon: '💫',
    baseStats: {
      health: 900,
      attack: 82,
      defense: 32,
      speed: 3,
      range: 2,
    },
    abilities: ['divine_judgement', 'fallen_grace'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'divineEssence', chance: 0.60, minQty: 3, maxQty: 6 },
      { item: 'cosmicOre', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'superiorHealthPotion', chance: 0.80, minQty: 2, maxQty: 5 },
    ],
  },

  celestialAbomination: {
    id: 'celestialAbomination',
    name: 'Celestial Abomination',
    icon: '👹',
    baseStats: {
      health: 950,
      attack: 78,
      defense: 36,
      speed: 2,
      range: 1,
    },
    abilities: ['holy_corruption', 'divine_crush'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'eterniumIngot', chance: 0.25, minQty: 1, maxQty: 2 },
      { item: 'starMetal', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'elixirOfPower', chance: 0.65, minQty: 1, maxQty: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // DIMENSIONAL RIFT MOBS (Reality-bending theme)
  // ═══════════════════════════════════════════════════════════════

  // Fracture Point (Lv 85) - Entry dimensional
  realityGlitch: {
    id: 'realityGlitch',
    name: 'Reality Glitch',
    icon: '📍',
    baseStats: {
      health: 560,
      attack: 68,
      defense: 18,
      speed: 6,
      range: 2,
    },
    abilities: ['phase_shift'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'cosmicFragment', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.65, minQty: 2, maxQty: 4 },
    ],
  },

  temporalAnomaly: {
    id: 'temporalAnomaly',
    name: 'Temporal Anomaly',
    icon: '⏳',
    baseStats: {
      health: 600,
      attack: 64,
      defense: 22,
      speed: 4,
      range: 2,
    },
    abilities: ['time_skip'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'voidEssence', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'elixirOfPower', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  // Twisted Plane (Lv 90) - Mid-level dimensional
  dimensionalHorror: {
    id: 'dimensionalHorror',
    name: 'Dimensional Horror',
    icon: '🕳️',
    baseStats: {
      health: 680,
      attack: 72,
      defense: 24,
      speed: 3,
      range: 2,
    },
    abilities: ['dimension_rip', 'planar_shift'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'cosmicFragment', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'eldritchHeart', chance: 0.30, minQty: 1, maxQty: 2 },
    ],
  },

  spaceWeaver: {
    id: 'spaceWeaver',
    name: 'Space Weaver',
    icon: '🕸️',
    baseStats: {
      health: 640,
      attack: 76,
      defense: 20,
      speed: 4,
      range: 3,
    },
    abilities: ['spatial_web', 'teleport'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'voidEssence', chance: 0.50, minQty: 2, maxQty: 5 },
      { item: 'scrollOfFortitude', chance: 0.55, minQty: 2, maxQty: 4 },
    ],
  },

  // Reality Core (Lv 95) - Elite dimensional
  realityEater: {
    id: 'realityEater',
    name: 'Reality Eater',
    icon: '👄',
    baseStats: {
      health: 760,
      attack: 80,
      defense: 26,
      speed: 3,
      range: 2,
    },
    abilities: ['consume_reality', 'existence_drain'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'cosmicOre', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'eldritchHeart', chance: 0.35, minQty: 1, maxQty: 2 },
    ],
  },

  planarArchitect: {
    id: 'planarArchitect',
    name: 'Planar Architect',
    icon: '🔧',
    baseStats: {
      health: 720,
      attack: 78,
      defense: 28,
      speed: 2,
      range: 3,
    },
    abilities: ['create_pocket_dimension', 'reality_reshape'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'cosmicFragment', chance: 0.60, minQty: 3, maxQty: 5 },
      { item: 'eterniumIngot', chance: 0.20, minQty: 1, maxQty: 2 },
    ],
  },

  // Dimensional Rift (Lv 100) - Boss area mobs
  dimensionShatterer: {
    id: 'dimensionShatterer',
    name: 'Dimension Shatterer',
    icon: '💥',
    baseStats: {
      health: 860,
      attack: 84,
      defense: 30,
      speed: 3,
      range: 2,
    },
    abilities: ['shatter_reality', 'dimensional_collapse'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'cosmicOre', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'cosmicFragment', chance: 0.60, minQty: 3, maxQty: 6 },
      { item: 'blessingToken', chance: 0.50, minQty: 1, maxQty: 3 },
    ],
  },

  infiniteOne: {
    id: 'infiniteOne',
    name: 'The Infinite One',
    icon: '♾️',
    baseStats: {
      health: 920,
      attack: 80,
      defense: 34,
      speed: 2,
      range: 2,
    },
    abilities: ['infinite_recursion', 'eternal_loop'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'eterniumIngot', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'voidEssence', chance: 0.55, minQty: 2, maxQty: 5 },
      { item: 'luckyCharm', chance: 0.60, minQty: 1, maxQty: 3 },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // CHAOS NEXUS MOBS (Pure entropy/chaos theme)
  // ═══════════════════════════════════════════════════════════════

  // Entropy Gate (Lv 85) - Entry chaos creatures
  chaosSpawn: {
    id: 'chaosSpawn',
    name: 'Chaos Spawn',
    icon: '🌀',
    baseStats: {
      health: 580,
      attack: 66,
      defense: 20,
      speed: 5,
      range: 1,
    },
    abilities: ['random_mutation'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'voidEssence', chance: 0.45, minQty: 2, maxQty: 4 },
      { item: 'superiorHealthPotion', chance: 0.65, minQty: 2, maxQty: 4 },
    ],
  },

  entropyWisp: {
    id: 'entropyWisp',
    name: 'Entropy Wisp',
    icon: '✨',
    baseStats: {
      health: 520,
      attack: 72,
      defense: 14,
      speed: 6,
      range: 2,
    },
    abilities: ['destabilize'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.85, minQty: 10, maxQty: 15 },
      { item: 'cosmicFragment', chance: 0.40, minQty: 2, maxQty: 4 },
      { item: 'elixirOfPower', chance: 0.50, minQty: 1, maxQty: 2 },
    ],
  },

  // Maelstrom Core (Lv 90) - Mid-level chaos
  chaosBeast: {
    id: 'chaosBeast',
    name: 'Chaos Beast',
    icon: '🐲',
    baseStats: {
      health: 720,
      attack: 74,
      defense: 22,
      speed: 4,
      range: 1,
    },
    abilities: ['chaos_breath', 'random_shift'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'eldritchHeart', chance: 0.35, minQty: 1, maxQty: 2 },
      { item: 'divineEssence', chance: 0.40, minQty: 2, maxQty: 4 },
    ],
  },

  madnessIncarnate: {
    id: 'madnessIncarnate',
    name: 'Madness Incarnate',
    icon: '🤪',
    baseStats: {
      health: 660,
      attack: 78,
      defense: 18,
      speed: 5,
      range: 2,
    },
    abilities: ['insanity_wave', 'unpredictable'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.88, minQty: 10, maxQty: 16 },
      { item: 'soulShard', chance: 0.55, minQty: 2, maxQty: 4 },
      { item: 'blessingToken', chance: 0.45, minQty: 1, maxQty: 2 },
    ],
  },

  // Primordial Chaos (Lv 95) - Elite chaos
  entropicTitan: {
    id: 'entropicTitan',
    name: 'Entropic Titan',
    icon: '🗿',
    baseStats: {
      health: 900,
      attack: 76,
      defense: 32,
      speed: 2,
      range: 1,
    },
    abilities: ['chaos_slam', 'reality_unravel'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'starMetal', chance: 0.40, minQty: 1, maxQty: 3 },
      { item: 'eterniumIngot', chance: 0.25, minQty: 1, maxQty: 2 },
    ],
  },

  chaosLord: {
    id: 'chaosLord',
    name: 'Chaos Lord',
    icon: '😈',
    baseStats: {
      health: 800,
      attack: 82,
      defense: 26,
      speed: 3,
      range: 2,
    },
    abilities: ['chaos_command', 'entropy_storm'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.90, minQty: 12, maxQty: 18 },
      { item: 'cosmicOre', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'voidEssence', chance: 0.55, minQty: 2, maxQty: 5 },
    ],
  },

  // Chaos Nexus (Lv 100) - Boss area mobs
  primordialChaos: {
    id: 'primordialChaos',
    name: 'Primordial Chaos',
    icon: '🌌',
    baseStats: {
      health: 880,
      attack: 86,
      defense: 30,
      speed: 3,
      range: 2,
    },
    abilities: ['creation_destruction', 'chaos_infusion'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'cosmicOre', chance: 0.40, minQty: 1, maxQty: 2 },
      { item: 'eldritchHeart', chance: 0.45, minQty: 1, maxQty: 3 },
      { item: 'superiorHealthPotion', chance: 0.80, minQty: 2, maxQty: 5 },
    ],
  },

  entropyAvatar: {
    id: 'entropyAvatar',
    name: 'Entropy Avatar',
    icon: '💀',
    baseStats: {
      health: 940,
      attack: 82,
      defense: 34,
      speed: 2,
      range: 2,
    },
    abilities: ['entropy_embrace', 'universe_end'],
    lootTable: 'legendary',
    drops: [
      { item: 'starEssence', chance: 0.92, minQty: 14, maxQty: 20 },
      { item: 'eterniumIngot', chance: 0.30, minQty: 1, maxQty: 2 },
      { item: 'divineEssence', chance: 0.50, minQty: 3, maxQty: 5 },
      { item: 'elixirOfPower', chance: 0.65, minQty: 1, maxQty: 3 },
    ],
  },
};

// ============================================================
// AGGREGATED EXPORTS
// ============================================================

/**
 * All enemy templates in one object
 * Organized by tier for dungeon generation
 */
export const ENEMY_TEMPLATES = {
  ...TIER1_MOBS,
  ...TIER2_MOBS,
  ...TIER3_MOBS,
  ...TIER4_MOBS,
  ...TIER5_MOBS,
  ...BOSS_MOBS,
};

/**
 * Mob pools organized by dungeon ID for easy lookup
 * Each dungeon has 2-3 themed mobs appropriate for its level
 * Keys match DUNGEON_CONFIG.TIERS[].id
 */
export const DUNGEON_MOB_POOLS = {
  // ═══════════════════════════════════════════════════════════════
  // TIER 1A - Ancient Ruins theme (Levels 5-20) - General undead
  // ═══════════════════════════════════════════════════════════════
  ancientRuins_lv5: ['cryptRat', 'cryptSpider'],
  ancientRuins_lv10: ['tombGuardian', 'dustyMummy'],
  ancientRuins_lv15: ['drownedZombie', 'vaultSlime'],
  ancientRuins_lv20: ['goblin', 'skeleton', 'wolf'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 1B - Goblin Cave theme (Levels 5-20) - Goblin-focused
  // ═══════════════════════════════════════════════════════════════
  goblinCave_lv5: ['goblinWhelp', 'caveBat'],
  goblinCave_lv10: ['goblinScout', 'goblinSapper'],
  goblinCave_lv15: ['goblinBrute', 'goblinWolfRider'],
  goblinCave_lv20: ['goblinChampion', 'goblinShaman'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 1C - Bandit Hideout theme (Levels 5-20) - Human outlaws
  // ═══════════════════════════════════════════════════════════════
  banditHideout_lv5: ['banditThug', 'guardDog'],
  banditHideout_lv10: ['banditArcher', 'banditCutthroat'],
  banditHideout_lv15: ['banditEnforcer', 'banditMage'],
  banditHideout_lv20: ['banditVeteran', 'banditAssassin'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 1D - Haunted Mine theme (Levels 5-20) - Undead miners
  // ═══════════════════════════════════════════════════════════════
  hauntedMine_lv5: ['mineRat', 'phosphorescentSlime'],
  hauntedMine_lv10: ['undeadMiner', 'mineSpider'],
  hauntedMine_lv15: ['phantomMiner', 'caveHorror'],
  hauntedMine_lv20: ['minerWraith', 'rockGolem'],

  // Legacy tier1_lv* keys for backwards compatibility
  tier1_lv5: ['cryptRat', 'cryptSpider'],
  tier1_lv10: ['tombGuardian', 'dustyMummy'],
  tier1_lv15: ['drownedZombie', 'vaultSlime'],
  tier1_lv20: ['goblin', 'skeleton', 'wolf'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 2A - Dark Catacombs theme (Levels 25-40) - Classic undead
  // ═══════════════════════════════════════════════════════════════
  darkCatacombs_lv25: ['boneWalker', 'bonePile'],
  darkCatacombs_lv30: ['cursedSkeleton', 'ossuaryKeeper'],
  darkCatacombs_lv35: ['graveLord', 'deathKnight'],
  darkCatacombs_lv40: ['orc', 'wraith', 'darkKnight'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 2B - Frozen Crypt theme (Levels 25-40) - Ice undead
  // ═══════════════════════════════════════════════════════════════
  frozenCrypt_lv25: ['frostZombie', 'iceSpider'],
  frozenCrypt_lv30: ['frozenWight', 'crystalElemental'],
  frozenCrypt_lv35: ['frostKnight', 'blizzardWraith'],
  frozenCrypt_lv40: ['iceReaper', 'glacialGolem'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 2C - Flooded Sewers theme (Levels 25-40) - Plague/sewer
  // ═══════════════════════════════════════════════════════════════
  floodedSewers_lv25: ['giantRat', 'sewerSlime'],
  floodedSewers_lv30: ['plagueZombie', 'toxicAlligator'],
  floodedSewers_lv35: ['swampHag', 'sewerAbomination'],
  floodedSewers_lv40: ['plagueLord', 'ratKing'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 2D - Orcish Stronghold theme (Levels 25-40) - Orc warriors
  // ═══════════════════════════════════════════════════════════════
  orcishStronghold_lv25: ['orcGrunt', 'orcArcher'],
  orcishStronghold_lv30: ['orcBerserker', 'warBoar'],
  orcishStronghold_lv35: ['orcWarboss', 'orcShaman'],
  orcishStronghold_lv40: ['orcChampion', 'orcWarlord'],

  // Legacy tier2_lv* keys for backwards compatibility
  tier2_lv25: ['boneWalker', 'bonePile'],
  tier2_lv30: ['cursedSkeleton', 'ossuaryKeeper'],
  tier2_lv35: ['graveLord', 'deathKnight'],
  tier2_lv40: ['orc', 'wraith', 'darkKnight'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 3A - Corrupted Temple theme (Levels 45-60) - Fallen divine
  // ═══════════════════════════════════════════════════════════════
  corruptedTemple_lv45: ['corruptedPriest', 'defiledAngel'],
  corruptedTemple_lv50: ['blightedMonk', 'plagueBearer'],
  corruptedTemple_lv55: ['demon', 'hellhound'],
  corruptedTemple_lv60: ['vampire', 'cultist'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 3B - Dragon's Lair theme (Levels 45-60) - Dragon cult
  // ═══════════════════════════════════════════════════════════════
  dragonsLair_lv45: ['dragonCultist', 'youngling'],
  dragonsLair_lv50: ['drakeGuard', 'firewyrm'],
  dragonsLair_lv55: ['lavaDrake', 'flameKnight'],
  dragonsLair_lv60: ['elderDrake', 'dragonPriest'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 3C - Demonic Sanctum theme (Levels 45-60) - Pure demons
  // ═══════════════════════════════════════════════════════════════
  demonicSanctum_lv45: ['imp', 'felGuard'],
  demonicSanctum_lv50: ['succubus', 'wrathGuard'],
  demonicSanctum_lv55: ['doomGuard', 'terrorFiend'],
  demonicSanctum_lv60: ['demonLord', 'felCaster'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 3D - Witch's Coven theme (Levels 45-60) - Dark magic
  // ═══════════════════════════════════════════════════════════════
  witchsCoven_lv45: ['hexedServant', 'cursedHound'],
  witchsCoven_lv50: ['covenAcolyte', 'scarecrow'],
  witchsCoven_lv55: ['blackWitch', 'nightHag'],
  witchsCoven_lv60: ['covenMatriarch', 'hauntedArmor'],

  // Legacy tier3_lv* keys for backwards compatibility
  tier3_lv45: ['corruptedPriest', 'defiledAngel'],
  tier3_lv50: ['blightedMonk', 'plagueBearer'],
  tier3_lv55: ['demon', 'hellhound'],
  tier3_lv60: ['vampire', 'cultist'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 4A - Abyssal Depths theme (Levels 65-80) - Deep void
  // ═══════════════════════════════════════════════════════════════
  abyssalDepths_lv65: ['shadowFiend', 'darkWraith'],
  abyssalDepths_lv70: ['nightmareBeast', 'fearWeaver'],
  abyssalDepths_lv75: ['pitLord', 'voidStalker'],
  abyssalDepths_lv80: ['abyssalKnight', 'shadowLord'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 4B - Infernal Fortress theme (Levels 65-80) - Fire demons
  // ═══════════════════════════════════════════════════════════════
  infernalFortress_lv65: ['flameSoldier', 'infernalHound'],
  infernalFortress_lv70: ['lavaDemon', 'fireElemental'],
  infernalFortress_lv75: ['flameLord', 'moltenGolem'],
  infernalFortress_lv80: ['archDemon', 'infernoKnight'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 4C - Shadow Realm theme (Levels 65-80) - Nightmare/shadow
  // ═══════════════════════════════════════════════════════════════
  shadowRealm_lv65: ['shadowStalker', 'nightmareSpawn'],
  shadowRealm_lv70: ['sleepParalyzer', 'phantasm'],
  shadowRealm_lv75: ['umbralReaper', 'dreamEater'],
  shadowRealm_lv80: ['nightmarePrince', 'voidbornHorror'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 4D - Eldritch Library theme (Levels 65-80) - Forbidden knowledge
  // ═══════════════════════════════════════════════════════════════
  eldritchLibrary_lv65: ['animatedTome', 'inkHorror'],
  eldritchLibrary_lv70: ['knowledgeSeeker', 'madScholar'],
  eldritchLibrary_lv75: ['mindFlayer', 'forbiddenOne'],
  eldritchLibrary_lv80: ['archScholar', 'boundEntity'],

  // Legacy tier4_lv* keys for backwards compatibility
  tier4_lv65: ['shadowFiend', 'darkWraith'],
  tier4_lv70: ['nightmareBeast', 'fearWeaver'],
  tier4_lv75: ['pitLord', 'voidStalker'],
  tier4_lv80: ['abyssalKnight', 'shadowLord'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 5A - Void Citadel theme (Levels 85-100) - Pure void
  // ═══════════════════════════════════════════════════════════════
  voidCitadel_lv85: ['riftWalker', 'dimensionalTear'],
  voidCitadel_lv90: ['astralWarden', 'prisonedOne'],
  voidCitadel_lv95: ['cosmicHorror', 'starforgedTitan'],
  voidCitadel_lv100: ['eternityWarden', 'voidEmperor'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 5B - Celestial Ruins theme (Levels 85-100) - Fallen divine
  // ═══════════════════════════════════════════════════════════════
  celestialRuins_lv85: ['fallenAngel', 'corruptedSeraph'],
  celestialRuins_lv90: ['wrathAngel', 'hollowGuardian'],
  celestialRuins_lv95: ['archSeraph', 'voidTouchedAngel'],
  celestialRuins_lv100: ['fallenDivinity', 'celestialAbomination'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 5C - Dimensional Rift theme (Levels 85-100) - Reality-bending
  // ═══════════════════════════════════════════════════════════════
  dimensionalRift_lv85: ['realityGlitch', 'temporalAnomaly'],
  dimensionalRift_lv90: ['dimensionalHorror', 'spaceWeaver'],
  dimensionalRift_lv95: ['realityEater', 'planarArchitect'],
  dimensionalRift_lv100: ['dimensionShatterer', 'infiniteOne'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 5D - Chaos Nexus theme (Levels 85-100) - Entropy/chaos
  // ═══════════════════════════════════════════════════════════════
  chaosNexus_lv85: ['chaosSpawn', 'entropyWisp'],
  chaosNexus_lv90: ['chaosBeast', 'madnessIncarnate'],
  chaosNexus_lv95: ['entropicTitan', 'chaosLord'],
  chaosNexus_lv100: ['primordialChaos', 'entropyAvatar'],

  // Legacy tier5_lv* keys for backwards compatibility
  tier5_lv85: ['riftWalker', 'dimensionalTear'],
  tier5_lv90: ['astralWarden', 'prisonedOne'],
  tier5_lv95: ['cosmicHorror', 'starforgedTitan'],
  tier5_lv100: ['eternityWarden', 'voidEmperor'],
};

/**
 * Boss pools organized by dungeon type
 * Each dungeon type can have its own themed boss
 */
export const DUNGEON_BOSS_POOLS = {
  // ═══════════════════════════════════════════════════════════════
  // TIER 1 BOSSES - One per dungeon type
  // ═══════════════════════════════════════════════════════════════
  ancientRuins: ['boss_ogre'],
  goblinCave: ['boss_goblinKing'],
  banditHideout: ['boss_banditLord'],
  hauntedMine: ['boss_mineOverseer'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 2 BOSSES - One per dungeon type
  // ═══════════════════════════════════════════════════════════════
  darkCatacombs: ['boss_lich'],
  frozenCrypt: ['boss_frostLich'],
  floodedSewers: ['boss_plagueRat'],
  orcishStronghold: ['boss_orcWarchief'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 3 BOSSES - One per dungeon type
  // ═══════════════════════════════════════════════════════════════
  corruptedTemple: ['boss_dragon'],
  dragonsLair: ['boss_elderWyrm'],
  demonicSanctum: ['boss_demonPrince'],
  witchsCoven: ['boss_grandWitch'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 4 BOSSES - One per dungeon type
  // ═══════════════════════════════════════════════════════════════
  abyssalDepths: ['boss_abyssalLord'],
  infernalFortress: ['boss_infernoLord'],
  shadowRealm: ['boss_nightmareKing'],
  eldritchLibrary: ['boss_madArchivist'],

  // ═══════════════════════════════════════════════════════════════
  // TIER 5 BOSSES - One per dungeon type
  // ═══════════════════════════════════════════════════════════════
  voidCitadel: ['boss_voidGod'],
  celestialRuins: ['boss_fallenSeraph'],
  dimensionalRift: ['boss_realityBreaker'],
  chaosNexus: ['boss_entropySovereign'],

  // Legacy tier keys for backwards compatibility
  tier1: ['boss_ogre', 'boss_goblinKing', 'boss_banditLord', 'boss_mineOverseer'],
  tier2: ['boss_lich', 'boss_frostLich', 'boss_plagueRat', 'boss_orcWarchief'],
  tier3: ['boss_dragon', 'boss_elderWyrm', 'boss_demonPrince', 'boss_grandWitch'],
  tier4: ['boss_abyssalLord', 'boss_infernoLord', 'boss_nightmareKing', 'boss_madArchivist'],
  tier5: ['boss_voidGod', 'boss_fallenSeraph', 'boss_realityBreaker', 'boss_entropySovereign'],
};

export default ENEMY_TEMPLATES;
